import ColumnAssignmentModal from 'common/components/dataUpload/ColumnAssignmentModal'
import DataUploadReport from 'common/components/dataUpload/DataUploadReport'
import NewPeopleModal, {type NewPeopleAnswer} from 'common/components/dataUpload/NewPeopleModal'
import SheetAssignmentModal from 'common/components/dataUpload/SheetAssignmentModal'
import {
  dispatchApi,
  isChunkedUploadAvailable,
  withUploadedFiles,
} from 'common/helpers/chunkedUpload'
import {
  ImportReadiness,
  extractImportReadiness,
  isInstituteExcelUploadResponse,
} from 'common/helpers/csvImportReadiness'
import {
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadAssignmentQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadColumnAssignments,
  type DataUploadConversion,
  type DataUploadDraftAnswers,
  type DataUploadHeldCell,
  type DataUploadCarried,
  type DataUploadNewStudent,
  type DataUploadNewTeacher,
  type DataUploadSheetAssignments,
  type DataUploadTeacherMatch,
  allocationAnswerKey,
  appendAllocationAssignments,
  appendApprovedNewTeachers,
  appendBranchAssignments,
  appendColumnAssignments,
  appendSheetAssignments,
  appendTeacherMatches,
  alreadyInListSplit,
  type DataUploadSkippedRow,
  isAlreadyInListRow,
  dataUploadErrorCode,
  dataUploadErrorConversion,
  dataUploadErrorIsInfo,
  dataUploadErrorMessage,
  dataUploadRetryable,
  dataUploadErrorQuestions,
  dataUploadErrorStatus,
  dataUploadFileExtension,
  dataUploadSkippedRowKey,
  isSpreadsheetUpload,
  normalizeAllocationQuestions,
  pendingQuestionsText,
  normalizeCarriedDrafts,
  normalizeDataUploadConversion,
  normalizeDataUploadDraftAnswers,
  normalizeNewStudents,
  normalizeNewTeachers,
  singleBranchAnswers,
  allocationLineCounts,
  askableAllocations,
  withoutAnsweredAllocations,
  withoutAnsweredBranches,
} from 'common/helpers/dataUpload'
import {ArrowLeft, ArrowRight, Upload, X} from 'lucide-react'
import React, {FC, ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  bulkCsvImport,
  confirmCsvImport,
  getCsvImportTableSchemas,
  uploadCsvImportFile,
} from 'setup/redux/Actions/CsvImport/csvImport'
import {getDataUploadDraft} from 'setup/redux/Actions/DataUpload/dataUpload'

import CsvColumnMapper from '../../../DataImport/CsvImport/components/CsvColumnMapper'
import CsvImportReview from '../../../DataImport/CsvImport/components/CsvImportReview'
import ImportReadinessPanel from '../../../DataImport/CsvImport/components/ImportReadinessPanel'
import ImportResultModal from '../../../DataImport/CsvImport/components/ImportResultModal'
import {saveDraftAnswers} from '../hooks/useOnboardingPrefillDraft'
import {STEP_TITLE_KEYS} from '../utils/stepTitles'
import {ALLOCATION_AUTO_IMPORT} from '../utils/teacherAllocationFlow'
import {ONBOARDING_QA_OCT18} from '../utils/onboardingQaOct18'
import {type TeacherEntryRow, normalizeExtractionResponse} from '../utils/teacherEntry'
import CarriedTeachersModal, {type CarriedTeachersChoice} from './CarriedTeachersModal'
import UploadStatusPanel, {type UploadStatusBusy, type UploadStatusItem} from './UploadStatusPanel'
import OnboardingUploader, {
  type UploaderFile,
  type UploaderHead,
  type UploaderRejectedFile,
} from './upload/OnboardingUploader'

// Mirrors the Excel Import module's own mapping restriction list (CsvImporter.tsx).
const RESTRICTED_TABLES = [
  'timetable_preferences',
  'student_subject',
  'subject',
  'user_teacher',
  'user_student',
  'class_room',
  'class',
  'sections',
  'timetable_subcategories',
  'timetable_categories',
]

interface TableMappings {
  [table: string]: {[key: string]: string}
}

interface PreviewData {
  [key: string]: string
}

/** A finished import: the file and the bulk-import answer, kept by the step (see Props). */
export interface AllocationImportDone {
  file: File
  result: any
  /** Rows the import refused. Only then does the file's line stay, with "View details". */
  failed: number
  /** Teacher assignments (class-subject lines with a teacher) the import sent. */
  assignments?: number
  /** Files read together for this import: the file and the others picked with it. */
  files?: number
  /** Distinct teachers across those lines; 0 when the payload does not say who they are. */
  teachers?: number
  /**
   * Teachers actually created for this file (`onSaveNewTeachers`, before the import), when the
   * save said; the import itself never creates any. Only these may be called "added".
   */
  createdTeachers?: number
  /**
   * The placed lines are in but some are still asked about ("Which classes?"): the grid shows
   * them now; the file is not done yet (its draft and prefill stay), and its answered lines
   * import after.
   */
  partial?: boolean
  /**
   * Lines the import left for later (QA 22-Oct): still waiting for classes — confirmed with none
   * (`pending`) or never answered — behind "N need your review", which reopens "Which classes?"
   * for them only. Never reopened on its own, and the file is not read again to show them.
   */
  reviewLeft?: DataUploadAllocationQuestion[]
  /** Lines whose subject the school doesn't have: listed once with their message, nothing to pick. */
  cantAssign?: AllocationLeftoverLine[]
  /** The spreadsheet tab the file was read with, re-sent when the lines left are answered. */
  sheet?: string
}

/** A line nothing can be assigned for: the teacher, the subject as written, and why. */
export interface AllocationLeftoverLine {
  teacher: string
  written: string
  message: string
}

/**
 * What an import leaves behind, from its read's "Which classes?" lines and the answers sent with
 * it: the lines still waiting for classes (none were chosen, or the line was never answered) and
 * the lines whose subject the school doesn't have. A line answered with classes was placed.
 */
export const allocationLeftovers = (
  questions: readonly DataUploadAllocationQuestion[] | null | undefined,
  answers: readonly DataUploadAllocationAssignment[] | null | undefined
): {reviewLeft: DataUploadAllocationQuestion[]; cantAssign: AllocationLeftoverLine[]} => {
  const placed = new Set(
    (answers ?? []).filter((answer) => answer.classIds.length > 0).map(allocationAnswerKey)
  )
  const reviewLeft = (questions ?? [])
    .map((question) => ({
      ...question,
      subjects: question.subjects.filter(
        (entry) =>
          Boolean(entry.subject) &&
          !placed.has(
            allocationAnswerKey({
              teacher: question.teacher,
              employeeId: question.employeeId,
              subject: entry.subject!,
            })
          )
      ),
    }))
    .filter((question) => question.subjects.length > 0)
  const cantAssign = (questions ?? []).flatMap((question) =>
    question.subjects
      .filter((entry) => !entry.subject)
      .map((entry) => ({
        teacher: question.teacher,
        written: entry.written,
        message: entry.issueMessage ?? '',
      }))
  )
  return {reviewLeft, cantAssign}
}

/** Lines in `allocationLeftovers().reviewLeft`: one per teacher + subject. */
export const allocationLineTotal = (questions: readonly DataUploadAllocationQuestion[] | undefined) =>
  (questions ?? []).reduce((sum, question) => sum + question.subjects.length, 0)

/**
 * Assignments and distinct teachers in a bulk-import payload's `subjects` lines, for the
 * "30 teachers, 78 assignments applied" summary. A line names its teacher by id, Employee ID or
 * name depending on the template, so the first of those it has is the key.
 */
/** Names listed under one left-out reason before "…and N more". */
const MAX_NAMES_PER_REASON = 6

const assignmentTotals = (payload: any): {assignments: number; teachers: number} => {
  const lines: any[] = Array.isArray(payload?.subjects) ? payload.subjects : []
  const teachers = new Set<string>()
  lines.forEach((line) => {
    const key = [
      line?.teacher_id,
      line?.teacher_employee_id,
      line?.employee_id,
      line?.teacher_cnic,
      line?.teacher,
      line?.teacher_name,
      line?.teacherName,
    ]
      .map((value) => String(value ?? '').trim())
      .find(Boolean)
    if (key) teachers.add(key.toLowerCase())
  })
  return {assignments: lines.length, teachers: teachers.size}
}

interface Props {
  /** The file picked (or the Teachers step's prefill); upload starts when it arrives. */
  file: File | null
  isDark: boolean
  /** Drop the current file (the X on its line, or a question the user cancelled). */
  onClose: () => void
  /**
   * The import went through. The step keeps `done` and hands it back as `lastImport`: its
   * refresh re-hydrates the wizard, which remounts this tab, so state kept here would be lost.
   */
  onImported: (done: AllocationImportDone) => void
  /** Shown on the file's line, e.g. when the file is the Teachers step's prefill, not a pick. */
  notice?: string
  lastImport: AllocationImportDone | null
  onClearLastImport: () => void
  /**
   * Create these teachers with the wizard's normal teacher save (bulk-import), so the file's
   * lines find them when it is posted again. Rejects when the save fails (it has said why).
   * Resolves with how many were created, when the save says.
   */
  onSaveNewTeachers: (teachers: DataUploadNewTeacher[]) => Promise<number | void>
  /**
   * The file's new students (`newPeople.students`, another list in the same workbook), handed
   * over once its allocations are done here — imported, or nothing left to import — so the step
   * asks about them after, never before. A students-only file hands them over at once.
   */
  onNewStudents: (
    file: File,
    students: DataUploadNewStudent[],
    /** The Students step's carried draft of them (`carried.students`), when the read kept one. */
    carriedDraftId?: string | null
  ) => void
  /**
   * A file with no teaching assignments whose teachers the read kept for Add Teachers
   * (`carried.teachers`, AI server): that step opens them from the draft, not the file.
   */
  onCarriedTeachers?: (file: File, carried: DataUploadCarried) => void
  /**
   * Bug 527: a teachers-only file's teachers, read as Add Teachers reads them
   * (`carried.teachers.read`, AI server), put straight into Add Teachers' review list on
   * "Import teachers" — merged as Add Teachers merges its own rows — and their carried draft
   * closed. Nothing is handed to Add Teachers or read again.
   */
  onImportCarriedTeachers?: (rows: TeacherEntryRow[], draftId: string) => void
  /** Registration numbers (lower-cased) of the students already in the Students grid. */
  knownStudentNumbers: ReadonlySet<string>
  /**
   * The school's setup has a Students step (subject-wise schools). Class-wise schools don't:
   * a students list is then neither sent there nor offered to save.
   */
  hasStudentsStep: boolean
  /**
   * The other files picked with `file`: sent in the same request and read as one upload (a
   * teacher listed in one and placed in another is not asked about). One line each on the card.
   */
  extraFiles?: readonly File[]
  /**
   * One file's X when several are read together: the page reads the others again without it
   * (their pieces are cached). Left out, every line goes with the first one.
   */
  onRemoveFile?: (file: File) => void
  /**
   * Redesigned Teacher Allocation page: Map columns opens on the page under the file's line (in
   * the page's own cards and buttons) instead of a dialog, and a single branch is picked without
   * a "Branch Selection Required" card. Left out, the earlier dialog is used.
   */
  inlineMapper?: boolean
  /**
   * The shared onboarding uploader (12 Oct review, point 3): the same drop zone, limits, file
   * rows, progress and retry as Add Teachers, with this card's files in it. Left out, the earlier
   * UploadStatusPanel lines are shown (and files are picked elsewhere).
   */
  uploader?: AllocationUploader
  /**
   * Where the shared uploader goes (`ALLOCATION_UPLOADER_IN_CARD`): the landing's Upload card,
   * while Map columns, reports and questions stay here. `null` until the card is on the page.
   * Left out, the uploader is shown here with the rest.
   */
  uploaderTarget?: HTMLElement | null
  /**
   * A re-upload (AI server): the file's class subjects another teacher has now
   * (`report.heldByOther`). Not imported; the page asks whether to replace each teacher.
   */
  onHeldByOther?: (cells: DataUploadHeldCell[]) => void
}

/** What the page gives the shared uploader; the files and their states come from this card. */
export interface AllocationUploader {
  /** Files dropped or picked. With a file on the card already, they are read together with it. */
  onFiles: (files: File[]) => void
  accept: string
  formatsText: string
  limitsText: string
  onDownloadTemplate: () => void
  templateBusy?: boolean
  /** No new files now (the Teachers file's allocations are being prepared). */
  disabled?: boolean
  rejected?: UploaderRejectedFile[]
  onDismissRejected?: () => void
  guidance?: ReactNode
}

/** `nothing_new`: every line's class subject already has a teacher (`ALREADY_ASSIGNED`). */
type Phase = 'uploading' | 'sheets' | 'map' | 'review' | 'failed' | 'nothing_new'

/** Created / updated / failed counts of a bulk-import answer (as ImportResultModal reads them). */
const importCounts = (result: any) => {
  const total = (tables: unknown) =>
    Object.values((tables as Record<string, unknown[]>) || {}).reduce<number>(
      (sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0),
      0
    )
  const message = String(result?.message ?? '')
  const fromMessage = (pattern: RegExp) => {
    const match = message.match(pattern)
    return match ? parseInt(match[1], 10) : null
  }
  return {
    created: fromMessage(/created (\d+)/i) ?? total(result?.created),
    updated: fromMessage(/updated (\d+)/i) ?? total(result?.updated),
    errors:
      fromMessage(/error[s]? (\d+)/i) ?? (Array.isArray(result?.errors) ? result.errors.length : 0),
  }
}

/** The answers given for a file's questions, sent with every re-post of it. */
interface UploadAnswers {
  sheets?: DataUploadSheetAssignments
  branches?: DataUploadBranchAssignments
  columns?: DataUploadColumnAssignments
  /** Which classes each teacher + subject is taught in (`allocationAssignments`). */
  allocations?: DataUploadAllocationAssignment[]
  /** `newPeople.teachers` keys the user said to save (`approvedNewTeachers`). */
  approvedNewTeachers?: string[]
  /** Unknown names the user matched to a saved teacher (`teacherMatches`). */
  teacherMatches?: DataUploadTeacherMatch[]
  /** Keys the user left unticked or cancelled: not asked about again for this file (not sent). */
  declinedNewTeachers?: string[]
  /**
   * Branch questions the user skipped. A branch has no skip value to send, so they are kept here
   * only: a re-read that asks them again does not open the dialog for them (not sent).
   */
  skippedBranches?: string[]
  /** The file's new students were answered for: not asked about again for this file. */
  studentsDecided?: boolean
}

/** The new-teachers dialog when it has nobody to ask about: one list, not a new one per render. */
const NO_NEW_TEACHERS: DataUploadNewTeacher[] = []

/** The new-students dialog when it has nobody to show: one list, not a new one per render. */
const NO_NEW_STUDENTS: DataUploadNewStudent[] = []

/**
 * Per file, kept outside the card: it unmounts and remounts (the tab reopened, the Teachers-file
 * check hiding it a moment), and must not read the same file twice or forget what was answered.
 * `reads`: each `csv-import/upload` of the file, by what went with it — a remount shows that read
 * (or waits for it) instead of posting again. Dropped once the file is imported.
 */
const allocationReads = new WeakMap<File, Map<string, Promise<any>>>()
/** Every answer given for a file (see UploadAnswers), across remounts. */
const allocationAnswers = new WeakMap<File, UploadAnswers>()
/** Carried files whose draft was asked for the answers kept on it (once per file). */
const allocationAnswersLookedUp = new WeakSet<File>()
/**
 * Files a read has come back for. A file added later is read together with them (one upload),
 * but their pieces are cached, so they are not sent again: their lines keep the tick while the
 * new one reads, as on Add Teachers.
 */
const allocationReadFiles = new WeakSet<File>()

const hasKeys = (value: object | undefined | null) => Boolean(value) && Object.keys(value!).length > 0

/** The answers the server reads, as a draft keeps them (only the ones that hold something). */
const toDraftAnswers = (answers: UploadAnswers): DataUploadDraftAnswers => ({
  ...(hasKeys(answers.sheets) ? {sheetAssignments: answers.sheets} : {}),
  ...(hasKeys(answers.columns) ? {columnAssignments: answers.columns} : {}),
  ...(hasKeys(answers.branches) ? {branchAssignments: answers.branches} : {}),
  ...((answers.allocations?.length ?? 0) > 0
    ? {allocationAssignments: answers.allocations}
    : {}),
  ...((answers.teacherMatches?.length ?? 0) > 0 ? {teacherMatches: answers.teacherMatches} : {}),
})

/** A draft's stored answers, as this card keeps them. */
const fromDraftAnswers = (answers: DataUploadDraftAnswers): UploadAnswers => ({
  sheets: answers.sheetAssignments,
  columns: answers.columnAssignments,
  branches: answers.branchAssignments,
  allocations: answers.allocationAssignments,
  teacherMatches: answers.teacherMatches,
})

/** The other files read with a file (see `extraFiles`), by that file. */
const allocationCompanions = new WeakMap<File, readonly File[]>()
/** No other files: one list, not a new one per render. */
const NO_FILES: readonly File[] = []

/**
 * A spreadsheet in another layout leaves the user something to decide in Map columns: a column
 * that matched nothing, a match that is not certain, required data the file lacks (branch, level…
 * reported as warnings), or nothing matched at all. Otherwise the columns read cleanly and the
 * file can import from its line.
 */
export const mappingNeedsUser = (
  mappings: Record<string, string>,
  matches: Record<string, {confidence?: string} | undefined> | null | undefined,
  warnings: unknown[] | null | undefined
): boolean => {
  const columns = Object.keys(mappings)
  if (columns.length === 0) return true
  if (columns.some((column) => !mappings[column])) return true
  if (
    Object.values(matches ?? {}).some((match) => match?.confidence && match.confidence !== 'high')
  ) {
    return true
  }
  return (warnings?.length ?? 0) > 0
}

/** A file's new students from its last read, held until its allocations are done. */
const allocationHeldStudents = new WeakMap<File, DataUploadNewStudent[]>()
/** The Students step's carried draft of a file's students (`carried.students.draftId`). */
const allocationCarriedStudents = new WeakMap<File, string>()
/**
 * A file whose allocations were already read on the Students step (`carried.allocation`): it is
 * opened here from that draft (`draftId`, no file), not read again.
 */
const allocationCarriedDrafts = new WeakMap<File, string>()

/** Read `file` on this step from the Students step's carried draft `draftId`. */
export const openCarriedAllocation = (file: File, draftId: string) => {
  allocationCarriedDrafts.set(file, draftId)
}

/** The carried draft `file` is read from, if any (closed once it is imported). */
export const carriedAllocationDraftOf = (file: File): string | null =>
  allocationCarriedDrafts.get(file) ?? null

/** The questions dialog when no branch is asked about: one list, not a new one per render. */
const NO_BRANCH_QUESTIONS: DataUploadBranchQuestion[] = []

/** The questions dialog when no class is asked about: one list, not a new one per render. */
const NO_ALLOCATION_QUESTIONS: DataUploadAllocationQuestion[] = []

/** The teachers popup with nothing read yet: one list each, not a new one per render. */
const NO_TEACHER_ROWS: TeacherEntryRow[] = []
const NO_SKIPPED_ROWS: DataUploadSkippedRow[] = []

/**
 * The Teacher Allocation step's file upload, shown under its buttons like the Add Teachers flow
 * (UploadStatusPanel): the file shows Uploading, Reading, then its summary; the Map columns
 * screen opens as before and imports from there (no separate review or result dialog). No modal for the usual file: our template, or (on the AI servers)
 * any file the server converts into it; the server maps it and returns the bulk payload. Only a
 * plain spreadsheet in another layout still opens the column mapper (map -> review -> import),
 * since its columns must be matched by hand. The questions dialogs (whose list, which branch)
 * stay dialogs, as on the other steps.
 *
 * Underneath it is the Excel Import module's upload -> map columns -> review -> import flow. Hits the exact same APIs as
 * the module (csv-import/upload, csv-import/confirm, csv-import/bulk); only the chrome
 * (routes, stats, walkthrough) is left behind. See CsvImporter.tsx for the original.
 *
 * On the AI servers the file may also be a PDF, Word file, photo or scan: `csv-import/upload`
 * (with `source=onboarding`) converts it into the Teacher Allocation template first, then runs
 * the normal flow. The response is today's response plus a `conversion` field, shown here as a
 * report. Only existing class-subject slots are filled, only with teachers who already exist;
 * rows naming anything else are listed as left out, never created.
 */
// Injected server-side by excelBulkImportBuilder when the workbook names no level. Not a
// level the user ever typed, so it never counts as content.
const FABRICATED_LEVEL_NAME = 'Default Level'

/**
 * True when the server-built bulk payload carries nothing worth importing.
 *
 * An EMPTY Teacher Allocation template still comes back as a "successful" upload: the
 * onboarding upload path skips the completeness gate (`canProceed` is forced true) and the
 * payload builder fabricates a `Default Level` branch entry whenever the file names no
 * level. Posting that payload to bulk-import creates a real "Default Level" in the tenant,
 * which then surfaces on the Curriculum/Levels screens after the post-import re-hydration.
 * Blocking it here keeps the empty payload from ever reaching the API. The backend-side
 * fix is specced in docs/backend-specs/onboarding-empty-teacher-allocation-default-level.md.
 */
const isEmptyBulkImportPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return true
  const p = payload as Record<string, unknown>
  const rows = (key: string) => (Array.isArray(p[key]) ? (p[key] as unknown[]).length : 0)
  if (rows('teachers') + rows('classes') + rows('subjects') + rows('students') > 0) return false
  const branches = Array.isArray(p.branch) ? (p.branch as Array<Record<string, unknown>>) : []
  const hasRealLevel = branches.some((b) =>
    (Array.isArray(b?.levels) ? (b.levels as Array<Record<string, unknown>>) : []).some(
      (lvl) => String(lvl?.name ?? '').trim() !== FABRICATED_LEVEL_NAME
    )
  )
  return !hasRealLevel
}

const AllocationFileImport: FC<Props> = ({
  file,
  isDark,
  onClose,
  onImported,
  notice,
  lastImport,
  onClearLastImport,
  onSaveNewTeachers,
  onNewStudents,
  onCarriedTeachers,
  onImportCarriedTeachers,
  knownStudentNumbers,
  hasStudentsStep,
  extraFiles = NO_FILES,
  onRemoveFile,
  inlineMapper = false,
  uploader,
  uploaderTarget,
  onHeldByOther,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const [phase, setPhase] = useState<Phase>('uploading')
  /**
   * The files sent again with an answer (or a Skip): the cards stay as they are, with no reading
   * state, and the answer updates them in place (QA 436, 437). New files, X and Answer wait.
   */
  const [refreshing, setRefreshing] = useState(false)
  const [importing, setImporting] = useState(false)

  // Upload-response state (same shape the Excel Import module keeps).
  const [preview, setPreview] = useState<PreviewData[]>([])
  const [tableMappings, setTableMappings] = useState<TableMappings>({})
  const [columnMatches, setColumnMatches] = useState<{[key: string]: any}>({})
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMappings, setColumnMappings] = useState<{[csvColumn: string]: string}>({})
  const [warnings, setWarnings] = useState<string[]>([])
  const [foreignKeyOptions, setForeignKeyOptions] = useState<{[key: string]: any[]}>({})
  const [spreadsheetTabs, setSpreadsheetTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState<string | null>(null)
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [schemas, setSchemas] = useState<{[table: string]: string[]}>({})
  const [defaultValues, setDefaultValues] = useState<{[key: string]: any}>({})
  const [hasUnresolvedColumns, setHasUnresolvedColumns] = useState(false)

  // Institute-template flow: the backend maps the template server-side and returns a
  // ready-to-send bulk payload + readiness verdict instead of per-column matches.
  const [isInstituteExcelFlow, setIsInstituteExcelFlow] = useState(false)
  const [importReadiness, setImportReadiness] = useState<ImportReadiness | null>(null)
  const [bulkImportPayload, setBulkImportPayload] = useState<any>(null)
  /**
   * Nothing imports until these new teachers are saved (the readiness verdict says so). Kept
   * after "Don't save" so the file's line can ask about them again.
   */
  const [blockingTeachers, setBlockingTeachers] = useState<{
    teachers: DataUploadNewTeacher[]
    sheet?: string
  } | null>(null)

  /** The column mapper, for a spreadsheet in another layout only (not our template). */
  const [mapperOpen, setMapperOpen] = useState(false)
  /** The last import's full result (errors, missing references), on request. */
  const [detailsOpen, setDetailsOpen] = useState(false)

  /** Set when the server converted the file first (`result.conversion`). */
  const [conversion, setConversion] = useState<DataUploadConversion | null>(null)
  /**
   * A conversion that produced no usable rows (422 NO_ROWS_CONVERTED): the modal stays open
   * on its report, which says why each row was left out, instead of closing on a toast.
   */
  const [conversionError, setConversionError] = useState<string | null>(null)
  /**
   * Whether reading the failed file again can help: false for a damaged or mislabelled file
   * (the user replaces it instead of "Try again"); null while nothing failed.
   */
  const [failedRetryable, setFailedRetryable] = useState<boolean | null>(null)
  /** 0..1 while the file's pieces are being sent (AI servers); null otherwise. */
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  /**
   * NEEDS_ASSIGNMENT: the workbook holds several unlabelled lists, so nothing was read. Asked
   * with the same dialog as the Teachers and Students steps; the file is posted again with the
   * answer. `sheet` is the spreadsheet tab picked earlier, re-sent as it was.
   */
  const [sheetQuestion, setSheetQuestion] = useState<{
    file: File
    question: DataUploadAssignmentQuestion
    sheet?: string
  } | null>(null)
  /**
   * BRANCH_REQUIRED: the file does not say which branch its allocations are in and its classes
   * exist in several. ALLOCATION_CLASSES_REQUIRED: some lines name no class, or a class with
   * several sections (still unclear inside one branch). Asked with the questions dialog the
   * other steps use (branch cards, "which classes?" cards); then re-posted with the answers.
   */
  const [branchQuestion, setBranchQuestion] = useState<{
    file: File
    questions: DataUploadBranchQuestion[]
    allocations: DataUploadAllocationQuestion[]
    /** New teachers the asked lines name (`conversion.newPeople.teachers`), with Save ticks. */
    teachers: DataUploadNewTeacher[]
    sheet?: string
  } | null>(null)
  /**
   * The question (`sheetQuestion` or `branchQuestion`) whose dialog was closed with its X: it
   * stays as asked and nothing is read again; the file's line says "Waiting for your answer" and
   * its Answer reopens the same dialog. A new question is a new object, so its dialog opens.
   */
  const [parkedQuestion, setParkedQuestion] = useState<object | null>(null)
  /**
   * QA 22-Oct, one pass like Add Teachers: "Which classes?" answers (Confirm or Skip) were sent for
   * this file, so the read they come back with is imported straight away — no ready card, no
   * second Import click, no further read. Cleared once that import (or "nothing left") is in.
   */
  const importAfterAnswersRef = useRef<File | null>(null)
  /** The last read's "Which classes?" lines, by file: what an import leaves over comes from them. */
  const lastQuestionsRef = useRef<{
    file: File
    questions: DataUploadAllocationQuestion[]
    sheet?: string
  } | null>(null)
  /** Leftover lines ("N need your review") reopened in "Which classes?" from the cleared card. */
  const [leftoverOpen, setLeftoverOpen] = useState(false)
  /**
   * Every answer given for a file (whose list, which branch, which field). They go with every
   * re-post of that file, keyed by the `File` itself. A file carried from the Students step also
   * keeps them on its draft (`PUT data-upload/drafts/:id/answers`): offered again after a logout,
   * it is not asked again.
   */
  const answersRef = useRef(allocationAnswers)
  const rememberAnswers = (target: File, answers: UploadAnswers) => {
    const previous = answersRef.current.get(target) ?? {}
    const next: UploadAnswers = {
      sheets: {...(previous.sheets ?? {}), ...(answers.sheets ?? {})},
      branches: {...(previous.branches ?? {}), ...(answers.branches ?? {})},
      columns: {...(previous.columns ?? {}), ...(answers.columns ?? {})},
      // One answer per teacher + subject; a later one replaces an earlier one.
      allocations: Array.from(
        new Map(
          [...(previous.allocations ?? []), ...(answers.allocations ?? [])].map((answer) => [
            allocationAnswerKey(answer),
            answer,
          ])
        ).values()
      ),
      approvedNewTeachers: Array.from(
        new Set([...(previous.approvedNewTeachers ?? []), ...(answers.approvedNewTeachers ?? [])])
      ),
      // One saved teacher per unknown name; a later answer replaces an earlier one.
      teacherMatches: Array.from(
        new Map(
          [...(previous.teacherMatches ?? []), ...(answers.teacherMatches ?? [])].map((match) => [
            match.name.trim().toLowerCase(),
            match,
          ])
        ).values()
      ),
      declinedNewTeachers: Array.from(
        new Set([...(previous.declinedNewTeachers ?? []), ...(answers.declinedNewTeachers ?? [])])
      ),
      skippedBranches: Array.from(
        new Set([...(previous.skippedBranches ?? []), ...(answers.skippedBranches ?? [])])
      ),
      studentsDecided: Boolean(previous.studentsDecided || answers.studentsDecided),
    }
    answersRef.current.set(target, next)
    const carriedDraft = allocationCarriedDrafts.get(target)
    const serverAnswered =
      answers.sheets ||
      answers.columns ||
      answers.branches ||
      answers.allocations ||
      (answers.teacherMatches?.length ?? 0) > 0
    if (carriedDraft && serverAnswered) saveDraftAnswers(dispatch, carriedDraft, toDraftAnswers(next))
  }
  /** The grid's registration numbers as they are now (read from upload callbacks). */
  const knownStudentsRef = useRef(knownStudentNumbers)
  const hasStudentsStepRef = useRef(hasStudentsStep)
  const onNewStudentsRef = useRef(onNewStudents)
  const onHeldByOtherRef = useRef(onHeldByOther)
  const onCarriedTeachersRef = useRef(onCarriedTeachers)
  const onImportCarriedTeachersRef = useRef(onImportCarriedTeachers)
  const onImportedRef = useRef(onImported)
  /** The card's file as of the last render: null once the card was cleared (leftover answers). */
  const fileRef = useRef(file)
  /** The answers to the questions were sent: the card loads until the read and import are back. */
  const [placingAnswers, setPlacingAnswers] = useState(false)
  /**
   * Bug 527: a teachers-only file, read the way Add Teachers reads it (`carried.teachers.read`):
   * its rows, how many the school has already, its left-out rows; shown on this card with an
   * "Import teachers" button. `imported` once they went into Add Teachers' review list.
   */
  const [carriedTeachersRead, setCarriedTeachersRead] = useState<{
    draftId: string
    rows: TeacherEntryRow[]
    inList: number
    leftOut: DataUploadSkippedRow[]
    students: number
    imported: boolean
    /** "Which classes?" for the subjects and classes the file names (QA 555); [] when none. */
    allocationQuestions: DataUploadAllocationQuestion[]
  } | null>(null)
  /** QA 555: the teachers-only file's popup is open (it opens with the read; Cancel closes it). */
  const [carriedTeachersPopup, setCarriedTeachersPopup] = useState(false)
  /** `ALREADY_ASSIGNED`: the server's own sentence ("30 assignments: 30 already assigned…"). */
  const [nothingNewText, setNothingNewText] = useState<string | null>(null)
  /**
   * `ALL_LINES_SKIPPED` (with `nothing_new`): every line's "Which classes?" was skipped, so
   * nothing is allocated. Information, not a failure; its lines are listed as left out.
   */
  const [nothingAllocated, setNothingAllocated] = useState(false)
  /** `onClose` as of the last render, for the upload callback (memoized on first render). */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    // Declared before the upload effect below, so the files are known when it posts.
    if (file) allocationCompanions.set(file, extraFiles)
    onCloseRef.current = onClose
    knownStudentsRef.current = knownStudentNumbers
    hasStudentsStepRef.current = hasStudentsStep
    onNewStudentsRef.current = onNewStudents
    onHeldByOtherRef.current = onHeldByOther
    onCarriedTeachersRef.current = onCarriedTeachers
    onImportCarriedTeachersRef.current = onImportCarriedTeachers
    onImportedRef.current = onImported
    fileRef.current = file
  })
  /** A file's new students from its last read, held until its allocations are done. */
  const pendingStudentsRef = useRef(allocationHeldStudents)
  /** The file's allocations are done: its held students go to the step (once). */
  const releaseStudents = (target: File) => {
    const students = pendingStudentsRef.current.get(target)
    pendingStudentsRef.current.delete(target)
    if (!students || students.length === 0) return
    rememberAnswers(target, {studentsDecided: true})
    onNewStudentsRef.current(target, students, allocationCarriedStudents.get(target) ?? null)
  }
  /**
   * A read with no teaching assignments whose people were carried to their own steps (AI server:
   * no `newPeople`, `carried.teachers` / `carried.students`): said as information — "This file
   * has no teaching assignments. 6 teachers were sent to Add Teachers and 48 students to
   * Students." — never the new-people question or the "couldn't read assignments" error. The
   * teachers are handed to Add Teachers here; the Students step finds its draft when it opens.
   * False (nothing done) when nothing was carried.
   */
  const settleCarriedOnly = (target: File, carriedValue: unknown): boolean => {
    const carried = normalizeCarriedDrafts(carriedValue)
    const teachers = carried?.teachers?.records ?? 0
    const students = carried?.students?.records ?? 0
    if (teachers + students === 0) return false
    // QA 555: a students-only file whose students the server lists (`carried.students.students`):
    // the card clears and the students popup opens (Save students only…), as for any file's new
    // students; it imports them from the carried draft. Without the list: said as before.
    const carriedStudentsList = normalizeNewStudents({
      students: (carriedValue as {students?: {students?: unknown}} | null)?.students?.students,
    })
    if (teachers === 0 && carried?.students && carriedStudentsList.length > 0) {
      allocationCarriedStudents.set(target, carried.students.draftId)
      pendingStudentsRef.current.set(target, carriedStudentsList)
      allocationReads.delete(target)
      resetState()
      onCloseRef.current()
      releaseStudents(target)
      return true
    }
    // Bug 527: the file read as Add Teachers would read it (`carried.teachers.read`, AI server):
    // shown here, with the same counts and left-out rows, and imported from here — not handed to
    // Add Teachers. Without it (an older server, or the older panel): handed over as before.
    const read = (carriedValue as {teachers?: {read?: unknown}} | null)?.teachers?.read
    if (
      carried?.teachers &&
      teachers > 0 &&
      read &&
      uploader &&
      onImportCarriedTeachersRef.current
    ) {
      const extraction = normalizeExtractionResponse(read, target.name)
      // The left-out rows Add Teachers lists: each source's, else the file's report.
      const fromSources = extraction.sources.flatMap((source) => source.leftOut ?? [])
      setCarriedTeachersRead({
        draftId: carried.teachers.draftId,
        rows: extraction.rows,
        inList: extraction.rows.filter((row) => row._existingTeacherId != null).length,
        leftOut: fromSources.length > 0 ? fromSources : extraction.skipped,
        students,
        imported: false,
        allocationQuestions: extraction.allocationQuestions ?? [],
      })
      // QA 555: asked in a popup straight away; the card behind it stays as it was until then.
      setCarriedTeachersPopup(true)
      setNothingNewText(null)
      setNothingAllocated(true)
      setPhase('nothing_new')
      return true
    }
    if (carried?.teachers && teachers > 0) onCarriedTeachersRef.current?.(target, carried.teachers)
    setNothingNewText(
      intl.formatMessage(
        {
          id:
            teachers > 0 && students > 0
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.CARRIED_ONLY'
              : teachers > 0
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.CARRIED_ONLY_TEACHERS'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.CARRIED_ONLY_STUDENTS',
        },
        {teachers, students}
      )
    )
    setNothingAllocated(true)
    setPhase('nothing_new')
    return true
  }
  /** An answer's `carried.students`: kept with the file, for its students popup. */
  const rememberCarried = (target: File, carried: unknown) => {
    const draftId = normalizeCarriedDrafts(carried)?.students?.draftId
    if (draftId) allocationCarriedStudents.set(target, draftId)
  }
  /**
   * The new students of an answer's `newPeople` (one or more places), once each: not those
   * already in the Students grid (same registration number), and none once the user answered
   * for this file.
   */
  const newStudentsFor = (target: File, ...sources: unknown[]): DataUploadNewStudent[] => {
    // No Students step (a class-wise school): students are not part of its setup.
    if (!hasStudentsStepRef.current) return []
    if (answersRef.current.get(target)?.studentsDecided) return []
    const seen = new Set<string>()
    return sources.flatMap(normalizeNewStudents).filter((student) => {
      const number = (student.registrationNumber ?? '').trim().toLowerCase()
      if (number && knownStudentsRef.current.has(number)) return false
      const key =
        number || [student.name.toLowerCase(), student.class ?? '', student.section ?? ''].join('|')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }
  /**
   * The people the file names that the school has not saved, asked about in one dialog.
   * Teachers: its lines name them. `canContinueWithout`: the rest of the file imports without
   * them. "Save and assign" creates the ticked teachers first (the wizard's teacher save, with
   * their Teacher ID), then posts the same file again — with `approvedNewTeachers` — so their
   * lines match. The file's students are not asked here: they wait until its allocations are
   * done (see `releaseStudents`).
   */
  const [newPeopleQuestion, setNewPeopleQuestion] = useState<{
    file: File
    sheet?: string
    teachers: DataUploadNewTeacher[]
    canContinueWithout: boolean
  } | null>(null)
  const [savingNewTeachers, setSavingNewTeachers] = useState(false)
  /** Teachers the save before a file's import created, by file, for its "added" toast. */
  const createdTeachersRef = useRef(new Map<File, number>())
  const noteCreatedTeachers = (file: File, created: number | void) => {
    if (typeof created !== 'number') return
    createdTeachersRef.current.set(file, (createdTeachersRef.current.get(file) ?? 0) + created)
  }
  /**
   * The new teachers being added from either dialog (how many), from Continue until the re-read
   * that places their lines answers: the card is loading ("Adding 3 teachers…", then "Placing
   * their assignments…"), not the first read's failure. Null otherwise.
   */
  const [addingTeachers, setAddingTeachers] = useState<number | null>(null)

  const schemasFetchedRef = useRef(false)

  const textColor = isDark ? '#ffffff' : '#181C32'
  const mutedColor = isDark ? '#92929f' : '#7E8299'

  const resetState = useCallback(() => {
    setPhase('uploading')
    setImporting(false)
    setPreview([])
    setTableMappings({})
    setColumnMatches({})
    setCsvColumns([])
    setColumnMappings({})
    setWarnings([])
    setForeignKeyOptions({})
    setSpreadsheetTabs([])
    setSelectedTab(null)
    setSelectedTable(null)
    setDefaultValues({})
    setHasUnresolvedColumns(false)
    setIsInstituteExcelFlow(false)
    setImportReadiness(null)
    setBulkImportPayload(null)
    setBlockingTeachers(null)
    setConversion(null)
    setConversionError(null)
    setNothingNewText(null)
    setCarriedTeachersRead(null)
    setCarriedTeachersPopup(false)
    setPlacingAnswers(false)
    setNothingAllocated(false)
    setFailedRetryable(null)
    setUploadProgress(null)
    setMapperOpen(false)
  }, [])

  // Same rename rule as the Excel Import module: with sections but no grades, the
  // backend's `class` mapping actually targets grades.
  const transformTableMappings = (mappings: TableMappings): TableMappings => {
    if (!mappings) return {}
    if (mappings.sections && mappings.class && !mappings.grades) {
      const {class: classMapping, ...rest} = mappings
      return {...rest, grades: classMapping}
    }
    return mappings
  }

  const uploadFile = useCallback(
    (selectedFile: File, sheet?: string, quiet = false) => {
      // `quiet`: sent again with an answer, the card keeps its state until the answer is back.
      if (quiet) setRefreshing(true)
      else setPhase('uploading')
      const carriedDraft = allocationCarriedDrafts.get(selectedFile) ?? null
      // A carried draft opened with no answers here (after a logout): the ones given before are
      // on the draft. Asked once, then read with them; any failure reads it without.
      if (
        carriedDraft &&
        !answersRef.current.has(selectedFile) &&
        !allocationAnswersLookedUp.has(selectedFile)
      ) {
        allocationAnswersLookedUp.add(selectedFile)
        dispatchApi(dispatch, (ok, fail) => getDataUploadDraft(carriedDraft, ok, fail))
          .then((res: any) => {
            const draft = (res?.result ?? res)?.draft
            const stored = normalizeDataUploadDraftAnswers(draft?.answers)
            if (stored && !answersRef.current.has(selectedFile)) {
              answersRef.current.set(selectedFile, fromDraftAnswers(stored))
            }
          })
          .catch(() => undefined)
          .finally(() => uploadFile(selectedFile, sheet, quiet))
        return
      }
      // Every file picked with it goes in the same request, read as one upload.
      const together: readonly File[] = carriedDraft
        ? [selectedFile]
        : [selectedFile, ...(allocationCompanions.get(selectedFile) ?? [])]
      /** The file itself, or (sent in pieces first) its `uploadId`. */
      const request = (uploadIds: string[] | null) => {
        const formData = new FormData()
        // Read on the Students step already: finished from that draft, no file sent.
        if (carriedDraft) formData.append('draftId', carriedDraft)
        else if (uploadIds && uploadIds.length > 1) {
          formData.append('uploadIds', JSON.stringify(uploadIds))
        } else if (uploadIds?.length === 1) formData.append('uploadId', uploadIds[0])
        else if (together.length > 1) together.forEach((one) => formData.append('files', one))
        else formData.append('file', selectedFile)
        if (sheet) formData.append('sheet', sheet)
        // Earlier answers for this file (whose list, which branch, which field): the server
        // keeps none, so every re-post carries them.
        const answers = answersRef.current.get(selectedFile)
        appendSheetAssignments(formData, answers?.sheets)
        appendBranchAssignments(formData, answers?.branches)
        appendColumnAssignments(formData, answers?.columns)
        appendAllocationAssignments(formData, answers?.allocations)
        // The new teachers the user said to save: the server creates them and keeps their lines.
        appendApprovedNewTeachers(formData, answers?.approvedNewTeachers)
        // Unknown names matched to a saved teacher: their lines go on that teacher.
        appendTeacherMatches(formData, answers?.teacherMatches)
        // Onboarding uploads skip the strict completeness validation server-side;
        // the sidebar Excel Import module (no source flag) keeps it.
        formData.append('source', 'onboarding')
        // The server defaults to `allocation` for onboarding, but older builds (and the sidebar
        // Excel Import) need it explicit. Our own template is still passed through untouched.
        // Legacy .xls isn't a data-upload format.
        if (dataUploadFileExtension(selectedFile.name) !== 'xls') {
          formData.append('useCaseKey', 'allocation')
        }
        return dispatchApi(dispatch, (ok, fail) => uploadCsvImportFile(formData, ok, fail))
      }
      // nginx refuses bodies over 1 MB on the AI servers: the file goes in 512 KB pieces there
      // (a picked sheet again reuses the same upload). Legacy .xls isn't a data-upload format.
      const chunked =
        !carriedDraft &&
        isChunkedUploadAvailable() &&
        together.every((one) => dataUploadFileExtension(one.name) !== 'xls')
      // One read per file and answers: a read already made (or on its way) is reused.
      const reads = allocationReads.get(selectedFile) ?? new Map<string, Promise<any>>()
      allocationReads.set(selectedFile, reads)
      // What the server is sent (not the answers kept here only, like declined teachers).
      const sent = answersRef.current.get(selectedFile)
      const readKey = JSON.stringify([
        together.map((one) => one.name),
        sheet ?? null,
        sent?.sheets ?? {},
        sent?.branches ?? {},
        sent?.columns ?? {},
        sent?.allocations ?? [],
        sent?.approvedNewTeachers ?? [],
        sent?.teacherMatches ?? [],
      ])
      const pending: Promise<any> =
        reads.get(readKey) ??
        (chunked
          ? withUploadedFiles(
              dispatch,
              together,
              {useCaseKey: 'allocation', onProgress: setUploadProgress},
              (uploadIds) => {
                setUploadProgress(null)
                return request(uploadIds)
              }
            )
          : request(null))
      reads.set(readKey, pending)

      /** A quiet answer is back: the question it answered goes as this read's state comes in. */
      const settleQuiet = () => {
        if (!quiet) return
        setRefreshing(false)
        setAddingTeachers(null)
        setPlacingAnswers(false)
        setSheetQuestion(null)
        setBranchQuestion(null)
        setParkedQuestion(null)
      }

      pending.then(
        (response: any) => {
          settleQuiet()
          setUploadProgress(null)
          const responseData = response.result || response.data || response
          if (!(response.statusCode === 200 || response.status === 'true')) {
            setConversionError(
              response.result?.error ||
                response.message ||
                intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
            )
            setPhase('failed')
            return
          }

          const {
            preview: previewData,
            mappings,
            columnMatches: matches,
            sheets,
            warnings: responseWarnings,
            foreignKeyOptions: fkOptions,
          } = responseData

          together.forEach((one) => allocationReadFiles.add(one))
          const readConversion = normalizeDataUploadConversion(responseData?.conversion)
          setConversion(readConversion)
          // Class subjects another teacher has now: asked on the page, never imported over.
          if (readConversion?.report.heldByOther?.length) {
            onHeldByOtherRef.current?.(readConversion.report.heldByOther)
          }

          const instituteFlow = isInstituteExcelUploadResponse(responseData)
          setIsInstituteExcelFlow(instituteFlow)
          const serverPayload =
            responseData?.bulkImportPayload ?? responseData?.result?.bulkImportPayload ?? null
          let readiness = extractImportReadiness(responseData)
          let payloadToImport = serverPayload
          // Empty template: block instead of trusting the server's forced `canProceed`. The
          // server says why when it knows (no row matches an existing subject and teacher, no
          // row has a Level…); "no data rows" is only the fallback.
          if (instituteFlow && isEmptyBulkImportPayload(serverPayload)) {
            const serverReason =
              readiness?.canProceed === false ? String(readiness.summaryMessage ?? '').trim() : ''
            readiness = {
              ...(readiness ?? {}),
              canProceed: false,
              summaryMessage:
                serverReason || intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}),
              missingItems: [],
              warningMessage: undefined,
              warnings: [],
            }
            // Dropped, not kept: handleImport must have nothing to send even if a later
            // state change were to flip `blocked` off.
            payloadToImport = null
          }
          setImportReadiness(readiness)
          setBulkImportPayload(payloadToImport)
          // Lines naming teachers the school has not saved: every line (nothing can import yet)
          // or some (the rest can). Either way the user is asked whether to save them.
          // Each teacher is asked about once per file: a re-post still names the ones the user
          // left unticked, and those must not bring the dialog back.
          const given = answersRef.current.get(selectedFile)
          const answered = new Set([
            ...(given?.approvedNewTeachers ?? []),
            ...(given?.declinedNewTeachers ?? []),
          ])
          // Two places: our template's own lines (`newPeople`), and a converted file's lines,
          // which the conversion already dropped for their teacher (`conversion.newPeople`).
          const reported = new Map<string, DataUploadNewTeacher>()
          ;[
            ...normalizeNewTeachers(responseData?.newPeople),
            ...normalizeNewTeachers(responseData?.conversion?.newPeople),
          ].forEach((teacher) => {
            if (!reported.has(teacher.key)) reported.set(teacher.key, teacher)
          })
          const newTeachers = Array.from(reported.values()).filter(
            (teacher) => !answered.has(teacher.key)
          )
          // QA 22-Oct, ask first, everything at once (as Add Teachers): a read with lines that
          // need a look — proposed classes, or a line with no proposal (`unresolvedLines`) — opens
          // "Which classes?" before anything imports, listing every such line. Lines only waiting
          // for review (confirmed with no classes, `pending`) or that can't be assigned never open
          // it by themselves (`askableAllocations`); they are left over after the import.
          const readQuestions = normalizeAllocationQuestions(responseData?.conversion)
          lastQuestionsRef.current = {file: selectedFile, questions: readQuestions, sheet}
          /** This read carries "Which classes?" answers: it is imported in the same step. */
          const answersRead = importAfterAnswersRef.current === selectedFile
          /** Answers for the lines left over, from the cleared card: nothing is asked again. */
          const leftoverAnswers = answersRead && !fileRef.current
          const unclear = leftoverAnswers
            ? []
            : askableAllocations(withoutAnsweredAllocations(readQuestions, given?.allocations))
          if (unclear.length > 0) {
            setBranchQuestion({
              file: selectedFile,
              questions: [],
              allocations: unclear,
              // Teachers the asked lines name that the school hasn't saved: in the same dialog,
              // so their save and the answers go with one re-post.
              teachers: newTeachers,
              sheet,
            })
          }
          // Blocked because of those teachers: the server's summary names every one of them
          // (fifty names in a paragraph). They are listed in the new-teachers dialog; the card
          // and the readiness panel say it in one line.
          const serverSummary = String(readiness?.summaryMessage ?? '')
          const teacherBlocked =
            readiness?.canProceed === false &&
            Array.from(reported.values()).some(
              (teacher) => teacher.name && serverSummary.includes(teacher.name)
            )
          if (teacherBlocked) {
            readiness = {
              ...(readiness ?? {}),
              summaryMessage: intl.formatMessage({
                id: 'DATA_UPLOAD.CONVERT.ERROR.TEACHERS_NOT_IN_SCHOOL',
              }),
            }
            setImportReadiness(readiness)
          }
          setBlockingTeachers(
            teacherBlocked ? {teachers: Array.from(reported.values()), sheet} : null
          )
          // A workbook with a students sheet too: its students wait until the allocations are
          // imported (the step's own data first, the other steps' after).
          const newStudents = newStudentsFor(
            selectedFile,
            responseData?.newPeople,
            responseData?.conversion?.newPeople
          )
          if (newStudents.length > 0) pendingStudentsRef.current.set(selectedFile, newStudents)
          rememberCarried(selectedFile, responseData?.conversion?.carried)
          // The answers' read, with nothing left to ask: imported now, under the same loader — no
          // ready card, no second Import click, and this read is the one imported (never read
          // again). Nothing new in it (every answered line skipped): the file is done.
          // From the cleared card (leftover lines) there is no card to ask on: it imports what it
          // can, and what still waits stays behind "N need your review".
          if (
            answersRead &&
            (leftoverAnswers ||
              (instituteFlow && unclear.length === 0 && newTeachers.length === 0))
          ) {
            const importable = instituteFlow && readiness?.canProceed !== false
            if (importable && assignmentTotals(payloadToImport).assignments > 0) {
              importAnsweredRef.current(selectedFile, payloadToImport)
              return
            }
            if (importable || leftoverAnswers) {
              if (!importable) {
                toast.error(
                  String(readiness?.summaryMessage ?? '').trim() ||
                    intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'})
                )
                importAfterAnswersRef.current = null
                return
              }
              finishAnsweredImportRef.current(selectedFile)
              return
            }
            // Blocked on the card: it says why there, as after any read.
          }
          // No teaching assignments, its teachers and students carried to their steps: said so,
          // instead of the empty file's "couldn't read assignments".
          if (
            newTeachers.length === 0 &&
            newStudents.length === 0 &&
            unclear.length === 0 &&
            assignmentTotals(payloadToImport).assignments === 0 &&
            settleCarriedOnly(selectedFile, responseData?.conversion?.carried)
          ) {
            return
          }
          // Asked first; the Map columns screen opens once it is answered (see NewPeopleModal).
          // With "Which classes?" open they are asked there instead, in the same dialog.
          const askingNewPeople = newTeachers.length > 0 && unclear.length === 0
          if (askingNewPeople) {
            setNewPeopleQuestion({
              file: selectedFile,
              sheet,
              teachers: newTeachers,
              canContinueWithout: readiness?.canProceed !== false,
            })
          }
          setPreview(previewData || [])
          setWarnings(responseWarnings || [])
          setForeignKeyOptions(fkOptions || {})

          if (instituteFlow) {
            // Blocked or auto-mapped: either way the map phase renders the right panel.
            const hasMappingData =
              (mappings && Object.keys(mappings).length > 0) ||
              (matches && Object.keys(matches).length > 0)
            if (!hasMappingData || readiness?.canProceed === false) {
              setPhase('map')
              // Auto-mapped: the Map columns screen shows what it will import. Blocked: it shows
              // why (readiness panel), with Import disabled — as the Excel Import flow always has.
              // On the page (`inlineMapper`) there is nothing to decide here: the server built
              // the payload, and the file's line says what imports or why it can't.
              if (!askingNewPeople && !inlineMapper) setMapperOpen(true)
              return
            }
          }

          if (!instituteFlow && sheets && sheets.length > 0) {
            setSpreadsheetTabs(sheets)
            setPhase('sheets')
            setMapperOpen(true)
            return
          }

          const transformedMappings = transformTableMappings(mappings)
          setTableMappings(transformedMappings)
          setColumnMatches(matches || {})
          if (transformedMappings && Object.keys(transformedMappings).length > 0) {
            setSelectedTable(Object.keys(transformedMappings)[0])
          }

          if (!instituteFlow && (!matches || Object.keys(matches).length === 0)) {
            setConversionError(intl.formatMessage({id: 'CSV_IMPORT.ERROR.NO_COLUMN_MATCHES'}))
            setPhase('failed')
            return
          }

          // Initialize mappings from the matches, enforcing one-to-one like the module
          // (all user_teacher columns are exempt and may repeat).
          const columns = matches ? Object.keys(matches) : []
          setCsvColumns(columns)
          const initialMappings: {[key: string]: string} = {}
          const usedDbColumns = new Set<string>()
          const isUserTeacherColumn = (dbColumnName: string) =>
            !!dbColumnName && dbColumnName.includes('user_teacher')
          const sortedColumns = [...columns].sort(
            (a, b) => (matches?.[b]?.similarity ?? 0) - (matches?.[a]?.similarity ?? 0)
          )
          sortedColumns.forEach((csvCol) => {
            const match = matches?.[csvCol]
            let dbColumnName: string | null = null
            if (match) {
              const column =
                match.dbColumn || match.column || match.databaseColumn || match.targetColumn
              if (column && match.table) dbColumnName = `${match.table}.${column}`
            }
            if (
              dbColumnName &&
              (!usedDbColumns.has(dbColumnName) || isUserTeacherColumn(dbColumnName))
            ) {
              initialMappings[csvCol] = dbColumnName
              if (!isUserTeacherColumn(dbColumnName)) usedDbColumns.add(dbColumnName)
            } else {
              initialMappings[csvCol] = ''
            }
          })
          setColumnMappings(initialMappings)
          setPhase('map')
          // The Map columns screen: how the columns were read, before import. On the page
          // (`inlineMapper`) it opens only when there is something to decide — never for our
          // template / a converted file (the server built the payload), and for another layout
          // only when a column is unmatched or uncertain, or required data is missing.
          const needsUser = instituteFlow
            ? false
            : mappingNeedsUser(initialMappings, matches, responseWarnings)
          if (!askingNewPeople && (!inlineMapper || needsUser)) setMapperOpen(true)
        },
        (error: any) => {
          settleQuiet()
          setUploadProgress(null)
          // A converted upload reports its failure as `result.code` (+ `result.conversion`
          // when rows were read but none matched the school). A chunked upload fails with
          // the same codes, or with no answer at all (a proxy page, a dropped connection).
          const code = dataUploadErrorCode(error)
          rememberCarried(selectedFile, error?.result?.conversion?.carried)
          // The answers' read (one pass) finding nothing new — every line placed, or each asked
          // line skipped: the file is done, not "already in the grid".
          if (
            quiet &&
            importAfterAnswersRef.current === selectedFile &&
            (code === 'ALREADY_ASSIGNED' || dataUploadErrorIsInfo(error))
          ) {
            // What it still leaves over (confirmed with no classes, can't be assigned); an answer
            // that lists none keeps the lines known so far.
            const stillOpen = normalizeAllocationQuestions(error?.result?.conversion)
            if (stillOpen.length > 0 || lastQuestionsRef.current?.file !== selectedFile) {
              lastQuestionsRef.current = {file: selectedFile, questions: stillOpen, sheet}
            }
            finishAnsweredImportRef.current(selectedFile)
            return
          }
          // Answers for the lines left over (the card is cleared): a failure is said, nothing else.
          if (quiet && !fileRef.current && importAfterAnswersRef.current === selectedFile) {
            importAfterAnswersRef.current = null
            toast.error(
              dataUploadErrorMessage(intl.formatMessage, error, {
                useCaseKey: 'allocation',
                fileName: selectedFile.name,
              })
            )
            return
          }
          // Nothing was read yet: a question comes back instead. Ask it, then post the same
          // file again with the answer (the next read may still ask the other one).
          if (
            code === 'NEEDS_ASSIGNMENT' ||
            code === 'BRANCH_REQUIRED' ||
            code === 'ALLOCATION_CLASSES_REQUIRED'
          ) {
            const asked = dataUploadErrorQuestions(error)
            // The lines asked: what an import of their answers may leave over (see `leftoverFields`).
            if (asked.allocations.length > 0) {
              lastQuestionsRef.current = {file: selectedFile, questions: asked.allocations, sheet}
            }
            const given = answersRef.current.get(selectedFile)
            // A branch the user skipped is not asked again: there is no answer to send for it.
            const open = withoutAnsweredBranches(asked.branches, given?.branches).filter(
              (question) => !given?.skippedBranches?.includes(question.key)
            )
            const allocations = askableAllocations(
              withoutAnsweredAllocations(asked.allocations, given?.allocations)
            )
            if (code === 'NEEDS_ASSIGNMENT' && asked.sheets) {
              setSheetQuestion({file: selectedFile, question: asked.sheets, sheet})
              return
            }
            // The asked lines' teachers the school has not saved come with the same refusal:
            // asked in the same dialog, so the answers and the save go with one re-post (not a
            // later TEACHERS_NOT_IN_SCHOOL). Each is asked about once per file.
            const decided = new Set([
              ...(given?.approvedNewTeachers ?? []),
              ...(given?.declinedNewTeachers ?? []),
            ])
            const teachers = normalizeNewTeachers(error?.result?.conversion?.newPeople).filter(
              (teacher) => !decided.has(teacher.key)
            )
            // One branch to choose from: that one, without asking.
            const single = singleBranchAnswers(open)
            const branches = open.filter((question) => !(question.key in single))
            if (Object.keys(single).length > 0) {
              rememberAnswers(selectedFile, {branches: single})
              if (branches.length === 0 && allocations.length === 0 && teachers.length === 0) {
                uploadFile(selectedFile, sheet, quiet)
                return
              }
            }
            if (branches.length > 0 || allocations.length > 0 || teachers.length > 0) {
              setBranchQuestion({
                file: selectedFile,
                questions: branches,
                allocations,
                teachers,
                sheet,
              })
              return
            }
          }
          // Nothing new is left (a re-upload, AI server): every line's class subject already has
          // its teacher, or another teacher the page asks about. Said as information, with the
          // server's own sentence — not as a failure.
          if (code === 'ALREADY_ASSIGNED') {
            const done = dataUploadErrorConversion(error)
            setConversion(done)
            const said = String(error?.result?.message ?? error?.message ?? done?.message ?? '')
            setNothingNewText(
              said.trim() ||
                intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.HEAD_ALREADY_ASSIGNED',
                })
            )
            const held = done?.report.heldByOther ?? []
            if (held.length > 0) onHeldByOtherRef.current?.(held)
            setPhase('nothing_new')
            return
          }
          // Every line's "Which classes?" was skipped (`ALL_LINES_SKIPPED`): nothing is allocated,
          // as the user chose. Said as information in the server's own words — not as a failed
          // file — with those lines listed as left out ("No classes chosen") under it.
          if (dataUploadErrorIsInfo(error)) {
            setConversion(dataUploadErrorConversion(error))
            setNothingNewText(
              dataUploadErrorMessage(intl.formatMessage, error, {
                useCaseKey: 'allocation',
                fileName: selectedFile.name,
              })
            )
            setNothingAllocated(true)
            setPhase('nothing_new')
            return
          }
          // No teaching assignments and no new teacher named, its people carried to their steps:
          // information (see `settleCarriedOnly`), not a failed file.
          // (Named new people — older servers — keep their questions below.)
          if (
            normalizeNewTeachers(error?.result?.conversion?.newPeople).length === 0 &&
            normalizeNewStudents(error?.result?.conversion?.newPeople).length === 0 &&
            settleCarriedOnly(selectedFile, error?.result?.conversion?.carried)
          ) {
            setConversion(dataUploadErrorConversion(error))
            return
          }
          // A converted file (the school's own layout) whose every line names a new teacher:
          // 422, the teachers in `conversion.newPeople`. Saved the same way: the file is posted
          // again with `approvedNewTeachers`.
          if (code === 'TEACHERS_NOT_IN_SCHOOL') {
            const given = answersRef.current.get(selectedFile)
            const answered = new Set([
              ...(given?.approvedNewTeachers ?? []),
              ...(given?.declinedNewTeachers ?? []),
            ])
            const missing = normalizeNewTeachers(error?.result?.conversion?.newPeople).filter(
              (teacher) => !answered.has(teacher.key)
            )
            const students = newStudentsFor(selectedFile, error?.result?.conversion?.newPeople)
            if (students.length > 0) pendingStudentsRef.current.set(selectedFile, students)
            if (missing.length > 0) {
              setNewPeopleQuestion({
                file: selectedFile,
                sheet,
                teachers: missing,
                canContinueWithout: false,
              })
            }
          }
          const converted = !isSpreadsheetUpload(selectedFile)
          const noAnswer =
            error instanceof Error ||
            [408, 413, 502, 504].includes(dataUploadErrorStatus(error) ?? 0)
          if (code || converted || (chunked && noAnswer)) {
            const failedConversion = dataUploadErrorConversion(error)
            // A students list: offer to save its students (on the Students step) rather than
            // only saying where it belongs. Students already in the grid are not offered.
            const listedStudents =
              code === 'NO_RELEVANT_DATA'
                ? newStudentsFor(selectedFile, error?.result?.conversion?.newPeople)
                : []
            if (listedStudents.length > 0) {
              // Nothing to import here: the card goes, and the step asks about the students.
              pendingStudentsRef.current.set(selectedFile, listedStudents)
              resetState()
              onCloseRef.current()
              releaseStudents(selectedFile)
              return
            }
            // Another screen's list (the file said so): say where to upload it. This step has no
            // prefill for other screens, so nothing was moved.
            const owner = failedConversion?.files.find(
              (f) => f.assignedTo && f.assignedTo !== 'allocation' && STEP_TITLE_KEYS[f.assignedTo]
            )
            if (code === 'NO_RELEVANT_DATA' && owner) {
              setConversion(null)
              setConversionError(
                // A class-wise school has no Students step to send a students list to.
                owner.assignedTo === 'students' && !hasStudentsStepRef.current
                  ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.STUDENTS_NOT_IN_SETUP'})
                  : intl.formatMessage(
                      {id: 'DATA_UPLOAD.CONVERT.BELONGS_TO_STEP'},
                      {step: intl.formatMessage({id: STEP_TITLE_KEYS[owner.assignedTo!]})}
                    )
              )
              setPhase('failed')
              return
            }
            const message = dataUploadErrorMessage(intl.formatMessage, error, {
              useCaseKey: 'allocation',
              fileName: selectedFile.name,
            })
            setFailedRetryable(dataUploadRetryable(code, undefined, error))
            if (failedConversion) {
              setConversion(failedConversion)
              setConversionError(message)
              setPhase('failed')
              return
            }
            setConversionError(message)
            setPhase('failed')
            return
          }
          const errData = error?.response?.data || error?.result || error
          setConversionError(
            errData?.result?.error ||
              errData?.error ||
              errData?.message ||
              error?.message ||
              intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
          )
          setPhase('failed')
        }
      )
    },
    [dispatch, inlineMapper, intl]
  )

  /**
   * The screens a sheet may be given to. csv-import/upload's NEEDS_ASSIGNMENT carries the sheets
   * but not the choices `data-upload/convert` sends beside them: the three screens then.
   */
  const sheetChoices = (question: DataUploadAssignmentQuestion): DataUploadAssignmentQuestion =>
    question.choices.length > 0
      ? question
      : {
          ...question,
          choices: [
            {
              key: 'teachers',
              title: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.TEACHERS'}),
            },
            {
              key: 'students',
              title: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.STUDENTS'}),
            },
            {
              key: 'allocation',
              title: intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.LESSON_PLANNING',
              }),
            },
          ],
        }

  // Kick the upload off whenever a fresh file arrives, or one read with it is taken off (the rest
  // are one upload again).
  const extraFilesKey = extraFiles.map((extra) => `${extra.name}::${extra.size}`).join('|')
  /** The files of the last read started here, to tell a file taken off from new ones. */
  const lastFilesRef = useRef<File[]>([])
  useEffect(() => {
    if (!file) return
    const now = [file, ...extraFiles]
    const before = lastFilesRef.current
    lastFilesRef.current = now
    // A file taken off (the X): the ones left were all read already, so they are read again
    // quietly — no reading state, the cards stay and update in place — as after an answer
    // (QA 436). A new file still reads as usual.
    const takenOff =
      before.length > now.length &&
      now.every((one) => before.includes(one) && allocationReadFiles.has(one))
    if (takenOff) {
      uploadFile(file, undefined, true)
      return
    }
    setSheetQuestion(null)
    setBranchQuestion(null)
    setParkedQuestion(null)
    setNewPeopleQuestion(null)
    resetState()
    uploadFile(file)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, extraFilesKey])

  // Table schemas feed the mapper's dropdowns; fetched once, filtered like the module.
  useEffect(() => {
    if (!file || schemasFetchedRef.current) return
    schemasFetchedRef.current = true
    dispatch(
      getCsvImportTableSchemas(
        (res: any) => {
          const allSchemas = res?.result?.schemas || res?.result || {}
          const filtered: {[table: string]: string[]} = {}
          Object.entries(allSchemas).forEach(([table, columns]) => {
            if (RESTRICTED_TABLES.includes(table)) {
              filtered[table] = columns as string[]
            }
          })
          setSchemas(filtered)
        },
        () => {}
      ) as never
    )
  }, [file, dispatch])

  /** What the import of `target`'s last read leaves over, as `AllocationImportDone` carries it. */
  const leftoverFields = (target: File): Partial<AllocationImportDone> => {
    const read = lastQuestionsRef.current?.file === target ? lastQuestionsRef.current : null
    const {reviewLeft, cantAssign} = allocationLeftovers(
      read?.questions,
      answersRef.current.get(target)?.allocations
    )
    return {
      ...(reviewLeft.length > 0 ? {reviewLeft} : {}),
      ...(cantAssign.length > 0 ? {cantAssign} : {}),
      ...(read?.sheet ? {sheet: read.sheet} : {}),
    }
  }

  /**
   * An import is in. QA 22-Oct: the card clears — no file lines, no ticks — and the step refreshes
   * the grid and says it in one toast ("29 assignments imported · 2 left for your review · 1 can't
   * be assigned"). What is left over goes with it (`reviewLeft`, `cantAssign`). `imported` and
   * `payload`: the answers' read imported straight away (`importAnswered`), not the card's state.
   */
  const handleImportSuccess = (
    response: any,
    imported: File | null = file,
    payload: unknown = bulkImportPayload
  ) => {
    const responseData = response.result || response.data || response
    const isSuccessResponse =
      response?.statusCode === 200 ||
      response?.status === 'true' ||
      response?.status === true ||
      response?.success === 'true' ||
      response?.success === true
    setImporting(false)
    if (!isSuccessResponse) {
      importAfterAnswersRef.current = null
      toast.error(response.message || intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'}))
      return
    }
    // Shown on the file's line (with "View details"), not in a result dialog to dismiss.
    const result = {...responseData, message: response.message || responseData.message || ''}
    const totals = assignmentTotals(payload)
    importAfterAnswersRef.current = null
    const leftovers = imported ? leftoverFields(imported) : {}
    // The allocations are in: now the file's students, if it lists any. Its reads are spent (a
    // later read must see what was just imported).
    if (imported) {
      releaseStudents(imported)
      allocationReads.delete(imported)
    }
    resetState()
    // Answers for the lines left over come from a card already cleared: nothing to close.
    if (file) onClose()
    if (imported) {
      const createdTeachers = createdTeachersRef.current.get(imported)
      createdTeachersRef.current.delete(imported)
      onImported({
        file: imported,
        result,
        failed: importCounts(result).errors,
        ...totals,
        files: 1 + (allocationCompanions.get(imported)?.length ?? 0),
        ...(createdTeachers != null ? {createdTeachers} : {}),
        ...leftovers,
      })
    }
  }

  /**
   * The answers' read (QA 22-Oct, one pass): its payload — the placed lines plus the answered
   * classes — is imported at once, under the same loader. Nothing is read again.
   */
  const importAnswered = (target: File, payload: unknown) => {
    setImporting(true)
    dispatch(
      bulkCsvImport(
        // source: 'onboarding' with no checkpoint: see handleImport.
        {...(payload as Record<string, unknown>), source: 'onboarding'},
        (response: any) => handleImportSuccess(response, target, payload),
        (error: any) => {
          importAfterAnswersRef.current = null
          handleImportFailure(error)
        }
      ) as never
    )
  }

  /**
   * The answers' read has nothing new to import (every answered line skipped, or placed already):
   * the file is done. The card clears; the step refreshes the grid and closes the file's draft,
   * with what is left over.
   */
  const finishAnsweredImport = (target: File) => {
    importAfterAnswersRef.current = null
    const leftovers = leftoverFields(target)
    releaseStudents(target)
    allocationReads.delete(target)
    resetState()
    if (fileRef.current) onCloseRef.current()
    onImportedRef.current({
      file: target,
      result: {},
      failed: 0,
      assignments: 0,
      files: 1 + (allocationCompanions.get(target)?.length ?? 0),
      ...leftovers,
    })
  }
  /** Called from a read's answer: the latest render's (see the effect below). */
  const importAnsweredRef = useRef(importAnswered)
  const finishAnsweredImportRef = useRef(finishAnsweredImport)
  useEffect(() => {
    importAnsweredRef.current = importAnswered
    finishAnsweredImportRef.current = finishAnsweredImport
  })

  const handleImportFailure = (error: any) => {
    setImporting(false)
    const responseData = error?.result || error?.data || error
    const readiness = extractImportReadiness(responseData) ?? extractImportReadiness(error)
    if (readiness) {
      setImportReadiness(readiness)
      if (readiness.canProceed === false) setPhase('map')
    }
    toast.error(
      readiness?.summaryMessage ||
        error?.message ||
        intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'})
    )
  }

  const handleImport = () => {
    if (!file) return

    if (isInstituteExcelFlow) {
      // Second line of defence for the empty-template case (see isEmptyBulkImportPayload).
      if (isEmptyBulkImportPayload(bulkImportPayload)) {
        toast.error(intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}))
        return
      }
      if (importReadiness?.canProceed === false) {
        toast.error(
          importReadiness.summaryMessage ||
            'Please complete all required data in the Excel file before continuing.'
        )
        return
      }
      setImporting(true)
      dispatch(
        bulkCsvImport(
          // source: 'onboarding' with no checkpoint marks this as the onboarding Excel import,
          // so node-api drops a "Default Level" nothing in the payload uses (backend spec
          // onboarding-empty-teacher-allocation-default-level.md, part C). Do not add checkpoint.
          {...(bulkImportPayload as Record<string, unknown>), source: 'onboarding'},
          handleImportSuccess,
          handleImportFailure
        ) as never
      )
      return
    }

    setImporting(true)

    // Rebuild tableMappings from the current columnMappings so manual changes made in
    // the mapper are reflected in the payload — same construction as the module.
    const finalTableMappings: TableMappings = {}
    Object.entries(columnMappings).forEach(([csvColumn, mapping]) => {
      if (!mapping) return
      const parts = mapping.split('.').filter(Boolean)
      if (parts.length < 2) return
      const dbColumn = parts[parts.length - 1]
      const tableName = parts[parts.length - 2]
      if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
      finalTableMappings[tableName][csvColumn] = dbColumn
    })

    if (defaultValues && Object.keys(defaultValues).length > 0) {
      Object.entries(defaultValues).forEach(([column, option]: [string, any]) => {
        let mappingKey: string
        let databaseColumn: string
        let tableName: string
        if (column === 'user_teacher') {
          mappingKey = 'teacher_cnic'
          databaseColumn = 'cnic'
          tableName = 'user_teacher'
        } else if (column === 'grades') {
          mappingKey = 'grades_name'
          databaseColumn = 'name'
          tableName = 'grades'
        } else {
          mappingKey = `${column}_name`
          databaseColumn = 'name'
          tableName = column
        }
        if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
        finalTableMappings[tableName][mappingKey] = databaseColumn
        const selectedValue = column === 'user_teacher' ? option.cnic : option.name
        preview.forEach((row) => {
          row[mappingKey] = selectedValue
        })
      })
    }

    const isExcelFile =
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      spreadsheetTabs.length > 0 ||
      !!selectedTab

    dispatch(
      confirmCsvImport(
        {
          data: preview,
          tableMappings: finalTableMappings,
          columnMappings,
          fileType: isExcelFile ? 'spreadsheet' : 'csv',
          dataType: 'all',
          targetTable: selectedTable,
          sheet: selectedTab,
          studentSubject: 'manual',
        },
        handleImportSuccess,
        handleImportFailure
      ) as never
    )
  }

  const blocked = importReadiness?.canProceed === false
  const showReadinessPanel =
    blocked ||
    Boolean(importReadiness?.warningMessage) ||
    (importReadiness?.warnings?.length ?? 0) > 0
  const autoMapped =
    isInstituteExcelFlow && csvColumns.length === 0 && Boolean(bulkImportPayload) && !blocked

  const isNextDisabled =
    phase !== 'map' || blocked || (!autoMapped && hasUnresolvedColumns) || importing
  const isImportDisabled =
    importing ||
    blocked ||
    (!isInstituteExcelFlow &&
      Object.values(columnMappings).filter((mapping) => mapping !== '').length === 0) ||
    (isInstituteExcelFlow && !bulkImportPayload)

  // The allocation lines bulk-import receives: one per class-subject slot with a teacher.
  const allocationCount = Array.isArray(bulkImportPayload?.subjects)
    ? bulkImportPayload.subjects.length
    : null
  /** Our template (or a converted file), read and mapped: its line imports it in place. */
  const inlineImport = isInstituteExcelFlow && (phase === 'map' || phase === 'review')
  /** A spreadsheet in another layout: its columns are matched in the mapper dialog. */
  const needsMapping = !isInstituteExcelFlow && ['sheets', 'map', 'review'].includes(phase)

  /**
   * Clear lines now arrive placed (the server resolves "Stage 8, sections A & B", ranges, "both
   * sections"…), so a read with nothing left to ask imports at once — no per-file "Import N
   * allocations" click. Anything still open waits for the user: a question dialog, teachers the
   * school doesn't have (NewPeopleModal), a blocked read, or the column mapper. Once per payload.
   */
  const autoImportedPayloadRef = useRef<unknown>(null)
  useEffect(() => {
    if (
      // `ALLOCATION_AUTO_IMPORT` off: the line's "Import N allocations" button imports.
      !ALLOCATION_AUTO_IMPORT ||
      !inlineImport ||
      phase !== 'map' ||
      blocked ||
      importing ||
      mapperOpen ||
      isImportDisabled ||
      newPeopleQuestion ||
      sheetQuestion ||
      branchQuestion ||
      !allocationCount ||
      autoImportedPayloadRef.current === bulkImportPayload
    ) {
      return
    }
    autoImportedPayloadRef.current = bulkImportPayload
    handleImport()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the read settles
  }, [
    allocationCount,
    blocked,
    branchQuestion,
    bulkImportPayload,
    importing,
    inlineImport,
    isImportDisabled,
    mapperOpen,
    newPeopleQuestion,
    phase,
    sheetQuestion,
  ])

  const importLabel =
    allocationCount !== null
      ? intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_ALLOCATIONS'},
          {count: allocationCount}
        )
      : intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})
  /**
   * Import: every file read together goes in one import. On the shared uploader it sits in the
   * headline, for all of them (not on the first file's line); on the old panel, on the line.
   */
  const importButton = (
    <button
      type='button'
      className='btn btn-sm btn-primary text-nowrap'
      disabled={isImportDisabled}
      onClick={handleImport}
    >
      {/* The shared uploader's headline already says "Importing…". */}
      {importing && !uploader ? (
        <span className='spinner-border spinner-border-sm me-2' role='status' />
      ) : null}
      {importLabel}
    </button>
  )

  /** Section -> row-count summary for a template the backend already auto-mapped. */
  const renderAutoMappedSummary = () => (
    <div
      style={{
        border: `1px solid ${isDark ? '#2b2b40' : '#e1e3ea'}`,
        borderRadius: 12,
        padding: '1.25rem 1.5rem',
      }}
    >
      <p className='mb-3' style={{color: textColor, fontWeight: 600}}>
        {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
      </p>
      <div className='d-flex flex-wrap gap-2'>
        {Object.entries(bulkImportPayload || {})
          .filter(([, value]) => Array.isArray(value))
          .map(([section, value]) => (
            <span
              key={section}
              className='badge badge-light-primary'
              style={{fontSize: '0.85rem', padding: '0.55rem 0.8rem'}}
            >
              {section}: {(value as any[]).length}
            </span>
          ))}
      </div>
      <p className='mt-3 mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
        {intl.formatMessage({id: 'CSV_IMPORT.INSTRUCTIONS.DOWNLOAD_TEMPLATE_ABOVE'})}
      </p>
    </div>
  )

  /**
   * The new-teachers dialog's answer. The ticked teachers are saved first; then the file is
   * posted again when they were saved to be assigned, or goes on to Map columns, or — nothing
   * left to import here — is dropped (and its students, if any, are asked about).
   */
  const answerNewPeople = (answer: NewPeopleAnswer) => {
    const pending = newPeopleQuestion
    if (!pending) return
    const picked = pending.teachers.filter((teacher) => answer.teacherKeys.includes(teacher.key))
    const approved = answer.teachers === 'assign' ? answer.teacherKeys : []
    /** "Match to existing": these names' lines go on the saved teacher picked. */
    const matched = answer.teacherMatches
    const matchedNames = new Set(matched.map((match) => match.name.trim().toLowerCase()))
    setSavingNewTeachers(true)
    if (picked.length > 0) setAddingTeachers(picked.length)
    ;(picked.length > 0 ? onSaveNewTeachers(picked) : Promise.resolve())
      .then((created) => {
        noteCreatedTeachers(pending.file, created)
        setSavingNewTeachers(false)
        setNewPeopleQuestion(null)
        rememberAnswers(pending.file, {
          approvedNewTeachers: approved,
          teacherMatches: matched,
          // Not asked about again for this file (a matched one leaves `newPeople` anyway).
          declinedNewTeachers: pending.teachers
            .filter(
              (teacher) =>
                !approved.includes(teacher.key) &&
                !matchedNames.has(teacher.name.trim().toLowerCase())
            )
            .map((teacher) => teacher.key),
        })
        if (answer.teachers === 'only' && picked.length > 0) {
          toast.success(
            intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.SAVED_ONLY'}, {count: picked.length})
          )
        }
        // They exist now (or are matched to a saved teacher): the re-read places their lines,
        // then Map columns opens.
        const repost = approved.length > 0 || matched.length > 0
        // Nothing left to import here: every line was a teacher saved without assigning (a
        // re-read would now allocate them, so it is not posted again).
        const finished = answer.teachers === 'only' && !pending.canContinueWithout
        // The card loads until the re-read answers (`settleQuiet`); with none, it is done now.
        if (!repost) setAddingTeachers(null)
        if (repost) uploadFile(pending.file, pending.sheet, true)
        else if (finished) {
          releaseStudents(pending.file)
          resetState()
          onClose()
        } else if (
          !blockingTeachers &&
          (!inlineMapper ||
            (!isInstituteExcelFlow && mappingNeedsUser(columnMappings, columnMatches, warnings)))
        ) {
          // On to Map columns, as after any read (blocked, it says why there). On the page, only
          // when there is something to decide (see the read above).
          setMapperOpen(true)
        }
        // Blocked by the teachers not saved: Map columns would only repeat that. The file's
        // line says it and offers to save them.
      })
      // A teacher save failed and said why; the dialog stays open to try again.
      .catch(() => {
        setSavingNewTeachers(false)
        setAddingTeachers(null)
      })
  }

  /**
   * The lines held back for each unknown teacher (`report.skipped[].newTeacherKey`), shown under
   * that teacher in the new-teachers dialog.
   */
  const heldLinesByTeacher: Record<string, string[]> = {}
  ;(conversion?.report.skipped ?? []).forEach((row) => {
    if (!row.newTeacherKey) return
    const label = String(row.label ?? '').trim()
    if (!label) return
    ;(heldLinesByTeacher[row.newTeacherKey] ??= []).push(label)
  })

  /** A question came back (its dialog open, or closed with the X): the read waits on the user. */
  const questionPending = Boolean(sheetQuestion || branchQuestion || newPeopleQuestion)
  /** Its dialog was closed with the X: the file's line offers Answer to reopen it. */
  const questionParked =
    !refreshing &&
    parkedQuestion !== null &&
    (parkedQuestion === sheetQuestion ||
      parkedQuestion === branchQuestion ||
      parkedQuestion === newPeopleQuestion)
  /** The file is being sent or read (not once a question came back instead). */
  const reading = phase === 'uploading' && !questionPending && !refreshing
  /** New teachers are being added, then their lines placed by the re-read: the card loads. */
  const addingBusy = addingTeachers !== null || placingAnswers
  const addingText = placingAnswers
    ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
    : addingTeachers !== null
    ? intl.formatMessage(
        {
          id: savingNewTeachers
            ? 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ADDING_TEACHERS'
            : 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.PLACING_ASSIGNMENTS',
        },
        {count: addingTeachers}
      )
    : null
  /**
   * The first read stopped for teachers the school has not saved: it waits for them, it did not
   * fail. Its error is not shown while they are asked about or being added.
   */
  const failureHidden = phase === 'failed' && (Boolean(newPeopleQuestion) || addingBusy)
  const failedShown = phase === 'failed' && !failureHidden

  /**
   * The open questions about one file of the upload: the branch questions it is asked
   * (`fileName`, or the "<fileName>::" key) and the teachers whose lines are in it (`fileNames`).
   * Null when the questions do not say which file they are about (one file, or an older server):
   * then they are the first line's.
   */
  const sameFileName = (a: unknown, b: string) =>
    String(a ?? '').trim().toLowerCase() === b.trim().toLowerCase()
  const questionsSayFile =
    Boolean(branchQuestion) &&
    [
      ...branchQuestion!.questions.map((question) => question.fileName),
      ...branchQuestion!.allocations.flatMap((question) => question.fileNames),
    ].some((name) => String(name ?? '').trim())
  const questionsAbout = (name: string) => {
    if (!branchQuestion || !questionsSayFile) return null
    const branches = branchQuestion.questions.filter(
      (question) =>
        sameFileName(question.fileName, name) ||
        sameFileName(question.key.replace(/::.*$/, ''), name)
    )
    const allocations = branchQuestion.allocations.filter((question) =>
      question.fileNames.some((fileName) => sameFileName(fileName, name))
    )
    return branches.length > 0 || allocations.length > 0 ? {branches, allocations} : null
  }
  /** What is open about `name` ("1 file needs a branch"), or null when nothing is. */
  const openAbout = (name: string): string | null => {
    const own = questionsAbout(name)
    return own ? pendingQuestionsText(intl.formatMessage, own) : null
  }
  /** Reopens the question's dialog, as it was (nothing read again). */
  const answerButton = (
    <button
      type='button'
      className='btn btn-sm btn-light-primary text-nowrap'
      onClick={() => setParkedQuestion(null)}
    >
      {intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.ANSWER_SHORT'})}
    </button>
  )

  const items: UploadStatusItem[] = []
  if (file) {
    const busy = reading || importing || addingBusy
    items.push({
      id: 'current',
      file,
      busy,
      locked: refreshing,
      busyText: addingText
        ? addingText
        : importing
        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
        : uploadProgress !== null
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
              {percent: Math.round(uploadProgress * 100)}
            )
          : intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
            }),
      progress: phase === 'uploading' ? uploadProgress : null,
      // While the new-teachers dialog asks, it is the message: nothing is repeated behind it.
      // Nor while they are being added: the line loads.
      error: newPeopleQuestion || addingBusy
        ? null
        : failedShown
          ? (conversionError ?? intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'}))
          : inlineImport && blocked
            ? String(importReadiness?.summaryMessage ?? '').trim() ||
              intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'})
            : null,
      // A question out: the line waits for its answer (Answer reopens a dialog closed with X).
      // Every line skipped: the server's sentence, as information.
      // On the page, columns that read cleanly need no matching, so the hint is left out.
      // What is open, named ("3 teachers need classes · 1 list needs a branch"); a sheet or
      // new-teachers question says "Waiting for your answer".
      hint: questionPending
        ? ((branchQuestion
            ? questionsSayFile
              ? openAbout(file.name)
              : pendingQuestionsText(intl.formatMessage, {
                  allocations: branchQuestion.allocations,
                  branches: branchQuestion.questions,
                })
            : null) ?? intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.WAITING'}))
        : phase === 'nothing_new' && nothingAllocated
          ? nothingNewText
          : needsMapping &&
              !(
                inlineMapper &&
                phase === 'map' &&
                !mappingNeedsUser(columnMappings, columnMatches, warnings)
              )
            ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.MATCH_COLUMNS_HINT'})
            : (notice ?? null),
      badges:
        inlineImport && !blocked && allocationCount !== null && !questionPending
          ? [
              {
                tone: 'success',
                text: intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ALLOCATIONS_READY'},
                  {count: allocationCount}
                ),
              },
            ]
          : [],
      action:
        questionPending ? (
          // The question's dialog was closed with its X: reopened as it was (nothing re-read).
          // With questions about other files only, the Answer is on their lines.
          questionParked && (!questionsSayFile || questionsAbout(file.name)) ? (
            answerButton
          ) : null
        ) : inlineImport && blocked && blockingTeachers ? (
          newPeopleQuestion ? null : (
            <button
              type='button'
              className='btn btn-sm btn-light-primary text-nowrap'
              disabled={savingNewTeachers}
              onClick={() =>
                setNewPeopleQuestion({
                  file: file,
                  sheet: blockingTeachers.sheet,
                  teachers: blockingTeachers.teachers,
                  canContinueWithout: false,
                })
              }
            >
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE'},
                {count: blockingTeachers.teachers.length}
              )}
            </button>
          )
        ) : inlineImport && blocked && !mapperOpen && !inlineMapper ? (
          // On the page the line's error says why; there is nothing to map.
          <button
            type='button'
            className='btn btn-sm btn-light-primary text-nowrap'
            onClick={() => setMapperOpen(true)}
          >
            {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
          </button>
        ) : inlineImport && !blocked && !(inlineMapper && mapperOpen) ? (
          // On the page: Import only — the server built the payload, nothing to decide. On the
          // shared uploader Import is in the headline (it is for every file).
          inlineMapper && uploader ? null : (
            <span className='d-flex flex-wrap gap-2'>
              {!inlineMapper && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary text-nowrap'
                  disabled={importing}
                  onClick={() => setMapperOpen(true)}
                >
                  {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
                </button>
              )}
              {!uploader && importButton}
            </span>
          )
        ) : needsMapping &&
          !mapperOpen &&
          inlineMapper &&
          phase === 'map' &&
          !mappingNeedsUser(columnMappings, columnMatches, warnings) ? (
          // On the page, columns that read cleanly import from the line: nothing to decide.
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            disabled={isImportDisabled}
            onClick={handleImport}
          >
            {importing ? (
              <span className='spinner-border spinner-border-sm me-2' role='status' />
            ) : null}
            {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})}
          </button>
        ) : needsMapping && !mapperOpen ? (
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            onClick={() => setMapperOpen(true)}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.MATCH_COLUMNS'})}
          </button>
        ) : null,
    })
  } else if (lastImport && lastImport.failed > 0) {
    // Only refused rows keep the file's line (with "View details"). Lines left over do not: the
    // card is cleared and they are said under it (`leftoverNotice`).
    const counts = importCounts(lastImport.result)
    const badges: Array<{text: string; tone: string}> = []
    if (counts.created > 0) {
      badges.push({
        tone: 'success',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_ADDED'},
          {count: counts.created}
        ),
      })
    }
    if (counts.updated > 0) {
      badges.push({
        tone: 'primary',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_UPDATED'},
          {count: counts.updated}
        ),
      })
    }
    if (counts.errors > 0) {
      badges.push({
        tone: 'danger',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_FAILED_ROWS'},
          {count: counts.errors}
        ),
      })
    }
    if (badges.length === 0) {
      badges.push({
        tone: 'success',
        text: intl.formatMessage({id: 'CSV_IMPORT.RESULT.IMPORT_COMPLETED'}),
      })
    }
    // Only an import with refused rows stays on the panel; a clean one leaves it at once.
    items.push({
      id: 'imported',
      file: lastImport.file,
      badges,
      action: (
        <button
          type='button'
          className='btn btn-sm btn-light-primary text-nowrap'
          onClick={() => setDetailsOpen(true)}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.VIEW_DETAILS'})}
        </button>
      ),
    })
  }

  // One line per file still to read, under the one being read (like the Students card).
  if (file) {
    // The files read with it: a line each. With `onRemoveFile` each has its own X (the rest are
    // read again); without, they are removed with it (they are one upload).
    extraFiles.forEach((extra, index) => {
      // Not while the upload is read or imported (as the first file's line).
      // A question about this file: its line says so, with its own Answer (one dialog for all).
      const open = questionPending ? openAbout(extra.name) : null
      items.push({
        id: `extra-${index}`,
        file: extra,
        locked: !onRemoveFile || reading || importing || refreshing,
        ...(open ? {hint: open, action: questionParked ? answerButton : null} : {}),
      })
    })
  }

  // One loader for the card, as on the Add Teachers card: the lines show no spinner of their own.
  const cardBusy: UploadStatusBusy | null =
    file && addingText
      ? {text: addingText, progress: null, hint: null}
      : file && (reading || importing)
      ? {
          text: importing
            ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
            : uploadProgress !== null
              ? intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
                  {percent: Math.round(uploadProgress * 100)}
                )
              : intl.formatMessage({
                  id:
                    extraFiles.length > 0
                      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING_FILES'
                      : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
                }),
          progress: !importing && phase === 'uploading' ? uploadProgress : null,
          // A photo or scan is read by AI, page by page: say it takes a while.
          hint:
            !importing && uploadProgress === null && !isSpreadsheetUpload(file)
              ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})
              : null,
        }
      : null

  // --- The shared uploader (`uploader`): this card's lines as its file rows and headline ------
  const ta = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.${id}`}, values)
  /** Something on the card waits for the user: a question, new teachers, a block, the mapper. */
  const awaitingUser =
    Boolean(newPeopleQuestion || sheetQuestion || branchQuestion) ||
    (inlineImport && blocked) ||
    (needsMapping && phase !== 'failed')
  /**
   * Read, but nothing in it can be imported and there is nothing to answer (no row matches what
   * is set up): a failed file, as on Add Teachers — the headline says so and the line offers a
   * fixed file — not "needs a decision".
   */
  const blockedWithoutQuestion =
    inlineImport &&
    blocked &&
    !blockingTeachers &&
    !newPeopleQuestion &&
    !sheetQuestion &&
    !branchQuestion

  /** One line of the card as a row of the shared uploader. */
  const toUploaderFile = (item: UploadStatusItem): UploaderFile => {
    const badgeText = (item.badges ?? []).map((badge) => badge.text).join(' · ')
    const base = {
      id: item.id,
      name: item.file.name,
      file: item.file,
      actions: item.action ?? null,
      // A file read with another goes with it; one being sent can't be removed mid-way.
      locked: Boolean(item.locked || item.busy),
    }
    // The files read together share the card's state: uploading, reading. On the shared uploader
    // an import spins only in the headline: the lines keep what they said.
    // A file read before, read again with a file added or taken off: it keeps its tick (not sent
    // again); only a new file spins.
    if (uploader && reading && item.id !== 'imported' && allocationReadFiles.has(item.file)) {
      return {...base, state: 'done', text: ''}
    }
    if ((item.busy || (item.locked && cardBusy)) && !(uploader && importing)) {
      const sending = phase === 'uploading' && !importing && uploadProgress !== null
      return {
        ...base,
        state: sending ? 'upload' : 'busy',
        text: cardBusy?.text ?? item.busyText ?? '',
        progress: sending ? uploadProgress : null,
        // How long a scan takes is not repeated under the file (shared uploader, as on Add Teachers).
        hint: null,
      }
    }
    // Nothing new in the file: a tick, with what the server said.
    // Every line skipped: information (no tick — nothing went in).
    // Bug 527: a teachers-only file's line, as Add Teachers words it ("Ready for review · 12 names",
    // or with its left-out rows), its left-out rows under it.
    if (item.id === 'current' && phase === 'nothing_new' && carriedTeachersRead) {
      const left = carriedTeachersRead.leftOut.length
      const names = carriedTeachersRead.rows.length
      const te = (id: string, values: Record<string, number>) =>
        intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values)
      return {
        ...base,
        state: left > 0 ? 'warning' : 'done',
        text:
          left > 0
            ? te('FILE_PARTIAL', {count: names, left})
            : te('FILE_READY_NAMES', {count: names}),
        details: renderLeftOut(false, carriedTeachersRead.leftOut),
      }
    }
    if (item.id === 'current' && phase === 'nothing_new') {
      return {
        ...base,
        state: nothingAllocated ? 'neutral' : 'done',
        text: nothingNewText ?? '',
        details: nothingAllocated ? renderLeftOut(true) : null,
      }
    }
    if (item.id === 'imported') {
      return {...base, state: lastImport && lastImport.failed > 0 ? 'error' : 'done', text: badgeText}
    }
    if (item.error) {
      return {
        ...base,
        state: 'error',
        text: item.error,
        hint: item.hint ?? null,
        // A failed read: why its rows were left out (that is what needs fixing), by reason.
        details: item.id === 'current' && phase === 'failed' ? renderLeftOut() : null,
        // A read that failed can be sent again; a blocked one needs its answer, not a retry.
        retryable: item.id === 'current' && phase === 'failed' && failedRetryable !== false,
        // A damaged or mislabelled file fails the same way again, and a file nothing in which
        // matches the setup needs fixing: replace it instead.
        replaceable:
          item.id === 'current' &&
          ((phase === 'failed' && failedRetryable === false) || blockedWithoutQuestion),
        replaceLabel: intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REPLACE_FILE',
        }),
      }
    }
    if (item.id === 'current' && awaitingUser) {
      return {...base, state: 'question', text: badgeText || item.hint || '', hint: null}
    }
    // Another file of the upload that is asked about itself: the same, on its own line.
    if (item.id.startsWith('extra-') && awaitingUser && item.hint) {
      return {...base, state: 'question', text: item.hint, hint: null}
    }
    if (item.id === 'current' && inlineImport && !blocked && allocationCount) {
      return {
        ...base,
        state: 'done',
        text: badgeText,
        hint: item.hint ?? null,
        details: renderLeftOut(),
      }
    }
    return {...base, state: 'neutral', text: badgeText || item.hint || '', hint: null}
  }

  /** Files read together: each line has its own count, and the upload's left-out rows go below. */
  const severalFiles = Boolean(file) && extraFiles.length > 0
  /** Every left-out row names its file: each file's go under its own line. */
  const leftOutSaysFile =
    Boolean(conversion?.report.skipped.length) &&
    Boolean(conversion?.report.skipped.every((row) => String(row.fileName ?? '').trim()))
  /**
   * The rows for the shared uploader. The files picked with the first one are read and imported
   * with it, so each takes its state (a tick, a question, a failure) instead of a bare "–"; what
   * the read found and the left-out rows stay on the first file's line.
   */
  const uploaderFiles = (): UploaderFile[] => {
    const rows = items.map(toUploaderFile)
    const current = rows.find((row) => row.id === 'current')
    if (!current) return rows
    const mirrored = rows.map((row) =>
      row.id.startsWith('extra-') && row.state === 'neutral' && current.state !== 'neutral'
        ? {...row, state: current.state}
        : row
    )
    // Several files read: the first one's line does not say the whole upload's count; the
    // headline has it. With `sources[]` (each file's own records and left-out rows) each line says
    // its own.
    if (!severalFiles || !conversion) return mirrored
    const sameName = (name: string | undefined, other: string) =>
      String(name ?? '').trim().toLowerCase() === other.trim().toLowerCase()
    const sourceOf = (name: string) =>
      conversion.sources.find((source) => sameName(source.fileName, name))
    const fileResultOf = (name: string) =>
      conversion.files.find((result) => sameName(result.fileName, name))
    const leftOutByFile = leftOutSaysFile
    return mirrored.map((row) => {
      const read = row.id === 'current' || row.id.startsWith('extra-')
      const settled = row.state === 'done' || row.state === 'warning'
      if (!read || !settled) return row
      const source = sourceOf(row.name)
      // The cells each file fills on the grid (`files[].counts.assignments`, AI server), never
      // its lines (`records`: one line can fill several classes). Without it, a tick. The
      // left-out rows do not say which file they came from: listed under the files.
      if (!source) {
        const assignments = fileResultOf(row.name)?.counts?.assignments
        return {
          ...row,
          text:
            typeof assignments === 'number'
              ? intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.FILE_ASSIGNMENTS_FOUND'},
                  {count: assignments}
                )
              : '',
          // Its own left-out rows, when they say whose they are (`skipped[].fileName`).
          details: leftOutByFile
            ? renderLeftOut(
                false,
                conversion.report.skipped.filter((skipped) => sameName(skipped.fileName, row.name))
              )
            : null,
        }
      }
      if (source.status === 'error') {
        return {
          ...row,
          state: 'error',
          text:
            source.message ||
            dataUploadErrorMessage(intl.formatMessage, source.reason ?? null, {
              useCaseKey: 'allocation',
              fileName: row.name,
            }),
          hint: null,
          details: renderLeftOut(true, source.leftOut ?? []),
        }
      }
      const records = source.records ?? 0
      const leftOutCount = source.leftOutCount ?? source.leftOut?.length ?? 0
      return {
        ...row,
        // Nothing kept from it, only rows left out: a warning, not a tick.
        state: leftOutCount > 0 && records === 0 ? 'warning' : 'done',
        text:
          records > 0
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ALLOCATIONS_READY'},
                {count: records}
              )
            : '',
        details: renderLeftOut(false, source.leftOut ?? []),
      }
    })
  }

  /**
   * The rows the read left out, under the file's line on the shared uploader — as Add Teachers
   * lists them (no "We read your file" report there). Open when nothing else went in.
   */
  const renderLeftOut = (open = false, rows?: readonly DataUploadSkippedRow[]): ReactNode => {
    if (!uploader || (!rows && !conversion)) return null
    const leftOut = rows
      ? rows.filter((row) => !isAlreadyInListRow(row))
      : conversion
        ? alreadyInListSplit(conversion).leftOut
        : []
    if (leftOut.length === 0) return null
    const unnamed = intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})
    // One entry per reason, not per row: "Two rows give different values for the same entry · 40"
    // with the names under it, each once (73 rows of the same few teachers read as a wall). The
    // reason is the row's translated code or reason, else the server's sentence.
    const groups = new Map<string, {reason: string; rows: number; names: string[]}>()
    leftOut.forEach((row) => {
      const key = dataUploadSkippedRowKey(row)
      const reason = key ? intl.formatMessage({id: key}) : row.message || row.reason || ''
      const groupKey = key ?? (row.code || row.reason || reason)
      const group = groups.get(groupKey) ?? {reason, rows: 0, names: []}
      group.rows += 1
      const name = String(row.label ?? '').trim() || unnamed
      if (!group.names.some((known) => known.toLowerCase() === name.toLowerCase())) {
        group.names.push(name)
      }
      groups.set(groupKey, group)
    })
    const ordered = Array.from(groups.values()).sort((a, b) => b.rows - a.rows)
    return (
      <details className='step4-te-file-skipped' open={open || undefined}>
        <summary>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
            {count: leftOut.length}
          )}
        </summary>
        <ul className='step4-te-file-skipped-groups'>
          {ordered.map((group) => (
            <li key={group.reason}>
              <div className='step4-te-file-skipped-reason'>
                <span>{group.reason}</span>
                <span className='step4-te-file-skipped-count'>{group.rows}</span>
              </div>
              <div className='step4-te-file-skipped-names'>
                {group.names.slice(0, MAX_NAMES_PER_REASON).join(', ')}
                {group.names.length > MAX_NAMES_PER_REASON &&
                  ` ${intl.formatMessage(
                    {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
                    {count: group.names.length - MAX_NAMES_PER_REASON}
                  )}`}
              </div>
            </li>
          ))}
        </ul>
      </details>
    )
  }

  /**
   * Bug 527: a teachers-only file's headline, in Add Teachers' words ("12 unique teachers ready for
   * review", "4 new teachers, 8 already in your list"), with Import teachers; then what went in.
   */
  const carriedTeachersHead = (read: NonNullable<typeof carriedTeachersRead>): UploaderHead => {
    const te = (id: string, values: Record<string, number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values)
    const tu = (id: string, values?: Record<string, number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.${id}`}, values)
    const studentsSent =
      read.students > 0 ? tu('CARRIED_STUDENTS_SENT', {students: read.students}) : null
    if (read.imported) {
      return {
        tone: 'ready',
        title: tu('CARRIED_TEACHERS_IMPORTED', {count: read.rows.length}),
        detail: studentsSent,
      }
    }
    const total = read.rows.length
    const fresh = Math.max(0, total - read.inList)
    const title =
      read.inList === 0
        ? te('PANEL_READY_TITLE', {count: total})
        : fresh > 0
          ? te('PANEL_NEW_AND_IN_LIST', {count: fresh, inList: read.inList})
          : te('PANEL_ALREADY_IN_LIST', {count: read.inList})
    return {
      tone: 'ready',
      title,
      detail: [tu('CARRIED_TEACHERS_HINT'), studentsSent].filter(Boolean).join(' '),
      action: (
        <button
          type='button'
          className='btn btn-sm btn-primary text-nowrap'
          disabled={total === 0}
          onClick={importCarriedTeachers}
        >
          {tu('IMPORT_TEACHERS')}
        </button>
      ),
    }
  }

  /** The card's Import teachers: the popup again (closed with Cancel earlier). */
  const importCarriedTeachers = () => {
    if (carriedTeachersRead && !carriedTeachersRead.imported) setCarriedTeachersPopup(true)
  }

  /**
   * QA 555, the popup's Confirm. The teachers go into Add Teachers' review list (merged as Add
   * Teachers merges its own; the wizard closes the carried draft and says "N teachers added from
   * your file"). Save teachers only: the card clears — no files, no ticks. Also choose their
   * classes: the "Which classes?" step opens for the subjects and classes the file names; its
   * answers read and import as any upload's (the grid refreshes, the card clears after).
   */
  const confirmCarriedTeachers = (choice: CarriedTeachersChoice) => {
    const read = carriedTeachersRead
    const target = file
    if (!read || read.imported || !target) return
    setCarriedTeachersPopup(false)
    onImportCarriedTeachersRef.current?.(read.rows, read.draftId)
    if (choice === 'classes' && read.allocationQuestions.length > 0) {
      setCarriedTeachersRead({...read, imported: true})
      // Its Confirm or Skip reads the answers and imports them in the same step (see
      // `importAfterAnswersRef`); a re-read with nothing to place finishes the card.
      setBranchQuestion({
        file: target,
        questions: [],
        allocations: read.allocationQuestions,
        teachers: [],
        sheet: undefined,
      })
      return
    }
    allocationReads.delete(target)
    resetState()
    onCloseRef.current()
  }

  /** The uploader's headline for this card: reading, a decision to make, ready, failed. */
  const uploaderHead = (): UploaderHead | null => {
    if (!file) {
      return lastImport && lastImport.failed > 0
        ? {tone: 'partial', title: ta('HEAD_IMPORTED_PARTIAL'), detail: ta('HEAD_IMPORTED_PARTIAL_HINT')}
        : null
    }
    const count = 1 + extraFiles.length
    // New teachers being added, then their lines placed: loading, not the first read's failure.
    if (addingText) return {tone: 'busy', title: addingText}
    if (reading || importing) {
      return {
        tone: 'busy',
        title: importing
          ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
          : ta('HEAD_READING', {count}),
        detail: importing
          ? null
          : ta(ALLOCATION_AUTO_IMPORT ? 'HEAD_READING_HINT' : 'HEAD_READING_HINT_REVIEW'),
      }
    }
    // Bug 527: a teachers-only file — Add Teachers' headline for it, and Import teachers.
    if (phase === 'nothing_new' && carriedTeachersRead)
      return carriedTeachersHead(carriedTeachersRead)
    // Every line skipped: said as information, in the server's words.
    if (phase === 'nothing_new' && nothingAllocated) {
      return {tone: 'empty', title: nothingNewText ?? ''}
    }
    if (phase === 'nothing_new') {
      return {
        tone: 'ready',
        title: ta('HEAD_ALREADY_ASSIGNED'),
        detail: nothingNewText,
      }
    }
    if (failedShown || blockedWithoutQuestion) {
      return {tone: 'failed', title: ta('HEAD_FAILED', {count}), detail: ta('HEAD_FAILED_HINT')}
    }
    if (awaitingUser) {
      return {
        tone: 'question',
        title: ta('HEAD_QUESTION', {count}),
        detail: ta(ALLOCATION_AUTO_IMPORT ? 'HEAD_QUESTION_HINT' : 'HEAD_QUESTION_HINT_REVIEW'),
      }
    }
    if (inlineImport && allocationCount) {
      // A re-upload: "30 assignments: 26 already assigned, 4 new" before the import hint.
      const already = conversion?.counts?.alreadyAssigned ?? 0
      const held = conversion?.counts?.heldByOther ?? 0
      // Cells another teacher keeps are said apart, never folded into the total as "new".
      const split =
        held > 0
          ? ta('ASSIGNMENT_SPLIT_HELD', {
              total: allocationCount + already + held,
              already,
              fresh: allocationCount,
              held,
            })
          : already > 0
            ? ta('ASSIGNMENT_SPLIT', {total: allocationCount + already, already, fresh: allocationCount})
            : null
      const hint = ALLOCATION_AUTO_IMPORT ? null : ta('HEAD_READY_HINT')
      // What else the files hold, as Add Teachers says it: the teachers the lines name, and the
      // students read for the Students step (`carried`, kept there as a draft).
      const {teachers} = assignmentTotals(bulkImportPayload)
      const students = conversion?.carried?.students?.records ?? 0
      const holds = [
        teachers > 0 ? ta('HEAD_READY_TEACHERS', {count: teachers}) : null,
        students > 0
          ? `${intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_STUDENTS'},
              {count: students}
            )}.`
          : null,
      ]
      // Proposed cells still waiting for "Which classes?": "60 ready · 18 need your review".
      // Older servers say it per file only (`files[].counts`): summed here.
      // Plus the lines that still need someone to look: confirmed with no classes (`pending`)
      // and lines with no proposal at all (`unresolvedLines`).
      const awaitingReview = ONBOARDING_QA_OCT18
        ? (conversion?.counts?.awaitingReview ??
            (Array.isArray((conversion as any)?.files)
              ? ((conversion as any).files as any[]).reduce(
                  (sum, entry) => sum + (Number(entry?.counts?.awaitingReview) || 0),
                  0
                )
              : 0)) +
          allocationLineCounts(conversion?.allocationQuestions).review +
          (conversion?.counts?.unresolvedLines ?? 0)
        : 0
      return {
        tone: 'ready',
        title:
          awaitingReview > 0
            ? ta('HEAD_READY_WITH_REVIEW', {count: allocationCount, review: awaitingReview})
            : ta('HEAD_READY', {count: allocationCount}),
        detail: [...holds, split, hint].filter(Boolean).join(' ') || null,
        // Blocked or the mapper open: nothing to import from here yet.
        action: !blocked && !(inlineMapper && mapperOpen) ? importButton : null,
      }
    }
    return null
  }

  /** "Try again" on a failed read: the same files, posted afresh (no cached read). */
  const retryRead = () => {
    if (!file || importing) return
    allocationReads.delete(file)
    // A branch skipped earlier is asked again: reading the file afresh is how it gets one.
    const given = answersRef.current.get(file)
    if (given?.skippedBranches?.length) answersRef.current.set(file, {...given, skippedBranches: []})
    resetState()
    uploadFile(file)
  }

  // --- Map columns: one content, shown on the page (`inlineMapper`) or in the earlier dialog ---
  const mapperShown = Boolean(file) && mapperOpen && (needsMapping || inlineImport)
  const mapperStepLabel =
    phase === 'review'
      ? intl.formatMessage({id: 'CSV_IMPORT.STEPS.REVIEW_IMPORT'})
      : intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})
  const backButtonClass = inlineMapper
    ? 'ta-btn ta-btn--outline me-auto'
    : 'onboarding-btn-back me-auto'
  const nextButtonClass = inlineMapper ? 'ta-btn ta-btn--primary' : 'onboarding-btn-next'

  const mapperBody = (
    <>
      {phase === 'sheets' && (
        <div className='d-flex flex-column gap-2'>
          <p style={{color: textColor, fontWeight: 600}}>
            {intl.formatMessage({id: 'CSV_IMPORT.STEPS.UPLOAD_FILE'})}
          </p>
          {spreadsheetTabs.map((tab) => (
            <button
              key={tab}
              type='button'
              className={
                inlineMapper
                  ? 'ta-btn ta-btn--ghost justify-content-start'
                  : 'btn btn-light-primary text-start'
              }
              onClick={() => {
                setSelectedTab(tab)
                if (file) uploadFile(file, tab)
              }}
            >
              <Upload size={14} className='me-2' aria-hidden />
              {tab}
            </button>
          ))}
        </div>
      )}

      {(phase === 'map' || phase === 'review') && showReadinessPanel && (
        <ImportReadinessPanel importReadiness={importReadiness} className='mb-4' />
      )}

      {phase === 'map' && !blocked && autoMapped && renderAutoMappedSummary()}

      {phase === 'map' && !blocked && !autoMapped && (
        <CsvColumnMapper
          csvColumns={csvColumns}
          columnMappings={columnMappings}
          columnMatches={columnMatches}
          schemas={schemas}
          selectedTable={selectedTable}
          tableMappings={tableMappings}
          warnings={warnings}
          foreignKeyOptions={foreignKeyOptions}
          compactSingleBranch={inlineMapper}
          onColumnMappingChange={(csvColumn, dbColumn) =>
            setColumnMappings((prev) => ({...prev, [csvColumn]: dbColumn}))
          }
          onDefaultValuesChange={setDefaultValues}
          onHasUnresolvedColumns={setHasUnresolvedColumns}
        />
      )}

      {phase === 'review' && (
        <CsvImportReview
          file={file}
          preview={preview}
          tableMappings={tableMappings}
          columnMappings={columnMappings}
          warnings={warnings}
          foreignKeyOptions={foreignKeyOptions}
          defaultValues={defaultValues}
          onDefaultValuesChange={setDefaultValues}
          // The allocation rows bulk-import actually receives, not the file's preview rows.
          recordCount={allocationCount ?? undefined}
        />
      )}
    </>
  )

  const importSpinner = importing ? (
    <span className='spinner-border spinner-border-sm me-2' role='status' />
  ) : null
  const mapperActions: ReactNode[] = []
  if (phase === 'review') {
    mapperActions.push(
      <button
        key='back'
        type='button'
        className={backButtonClass}
        disabled={importing}
        onClick={() => setPhase('map')}
      >
        <ArrowLeft size={16} className='me-2' />
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.BACK'})}
      </button>
    )
  }
  // Our template: imported from here (the server built it; no review step).
  if (phase === 'map' && isInstituteExcelFlow) {
    mapperActions.push(
      <button
        key='import'
        type='button'
        className={nextButtonClass}
        disabled={isImportDisabled}
        onClick={handleImport}
      >
        {importSpinner}
        {importLabel}
      </button>
    )
  }
  if (phase === 'map' && !isInstituteExcelFlow) {
    mapperActions.push(
      <button
        key='next'
        type='button'
        className={nextButtonClass}
        disabled={isNextDisabled}
        onClick={() => setPhase('review')}
      >
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.NEXT'})}
        <ArrowRight size={16} className='ms-2' />
      </button>
    )
  }
  if (phase === 'review') {
    mapperActions.push(
      <button
        key='import'
        type='button'
        className={nextButtonClass}
        disabled={isImportDisabled}
        onClick={handleImport}
      >
        {importSpinner}
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})}
      </button>
    )
  }

  const removeLine = (id: string) => {
    if (id === 'imported') {
      onClearLastImport()
      return
    }
    if (importing) return
    // Several files: only this one goes, and the others are read again without it.
    if (onRemoveFile && file && extraFiles.length > 0) {
      const target =
        id === 'current'
          ? file
          : id.startsWith('extra-')
            ? extraFiles[Number(id.slice('extra-'.length))]
            : undefined
      if (target) {
        // The answers are kept under the first file: the next one leads now, so it takes them
        // (a branch answer names its file, so it still fits) and nothing is asked again.
        const given = answersRef.current.get(file)
        if (target === file && given) rememberAnswers(extraFiles[0], given)
        onRemoveFile(target)
        return
      }
    }
    resetState()
    onClose()
  }
  /** Whose list each sheet is (Skip: every sheet `ignore`), sent once with a re-read. */
  const answerSheets = (assignments: DataUploadSheetAssignments) => {
    const pending = sheetQuestion
    if (!pending) return
    // Kept on the card until the answer is back (no reading state); the dialog closes.
    setParkedQuestion(pending)
    rememberAnswers(pending.file, {sheets: assignments})
    uploadFile(pending.file, pending.sheet, true)
  }
  /** Under the lines, in either panel: why a read failed or is blocked, and what it found. */
  const lineReports = (
    <>
      {/* The "We read your file" report is the old panel's: the shared uploader lists the
          left-out rows under the file's line instead, as Add Teachers does. */}
      {!uploader && file && failedShown && conversion && (
        <DataUploadReport conversion={conversion} isDark={isDark} />
      )}
      {/* Every line skipped: the lines left out, each "No classes chosen". */}
      {!uploader && file && phase === 'nothing_new' && nothingAllocated && conversion && (
        <DataUploadReport conversion={conversion} isDark={isDark} />
      )}

      {/* Blocked by new teachers: the file's line already says so in one sentence. */}
      {inlineImport && showReadinessPanel && !blockingTeachers && (
        <ImportReadinessPanel importReadiness={importReadiness} />
      )}
      {!uploader && inlineImport && conversion && (
        <DataUploadReport conversion={conversion} isDark={isDark} />
      )}
      {/* Several files, and the left-out rows do not say whose: listed once, under the files. */}
      {uploader &&
        severalFiles &&
        conversion &&
        conversion.sources.length < 2 &&
        !leftOutSaysFile &&
        !reading &&
        !addingBusy &&
        !failureHidden && (
        <div className='step4-te-upload-left-out'>{renderLeftOut()}</div>
      )}
    </>
  )

  /**
   * QA 22-Oct: what the last import left over, under the cleared card. "N need your review"
   * reopens "Which classes?" for those lines only (never on its own, and nothing is read to show
   * it); the lines that can't be assigned are listed once with their message. The X dismisses it.
   */
  const reviewLeftCount = file ? 0 : allocationLineTotal(lastImport?.reviewLeft)
  const cantAssignLeft = (!file && lastImport?.cantAssign) || []
  const leftoverBusy = !file && (placingAnswers || importing || refreshing)
  const answerLeftovers = (answers: DataUploadAllocationAssignment[]) => {
    const done = lastImport
    setLeftoverOpen(false)
    // Nothing picked: the lines stay as they are, behind the same link.
    if (!done || answers.every((answer) => answer.classIds.length === 0)) return
    // What the read is checked against: these lines, and the ones that can't be assigned.
    lastQuestionsRef.current = {
      file: done.file,
      sheet: done.sheet,
      questions: [
        ...(done.reviewLeft ?? []),
        ...(done.cantAssign ?? []).map((line, index) => ({
          key: `cant-${index}`,
          teacher: line.teacher,
          employeeId: null,
          fileNames: [],
          subjects: [
            {
              written: line.written,
              subject: null,
              classes: [],
              options: [],
              fromFile: false,
              ...(line.message ? {issueMessage: line.message} : {}),
            },
          ],
        })),
      ],
    }
    importAfterAnswersRef.current = done.file
    setPlacingAnswers(true)
    rememberAnswers(done.file, {allocations: answers})
    uploadFile(done.file, done.sheet, true)
  }
  const leftoverNotice =
    !file && lastImport && (reviewLeftCount > 0 || cantAssignLeft.length > 0) ? (
      <div className='step4-alloc-leftover' role='status'>
        <div className='step4-alloc-leftover__head'>
          {leftoverBusy ? (
            <span className='d-inline-flex align-items-center gap-2'>
              <span className='spinner-border spinner-border-sm text-primary' aria-hidden='true' />
              {ta('IMPORTING')}
            </span>
          ) : reviewLeftCount > 0 ? (
            <button
              type='button'
              className='btn btn-link p-0 step4-alloc-leftover__link'
              onClick={() => setLeftoverOpen(true)}
            >
              {ta('LEFT_NEED_REVIEW', {count: reviewLeftCount})}
            </button>
          ) : (
            <span className='step4-alloc-leftover__title'>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.QUESTIONS.LINES_CANT_BE_ASSIGNED'},
                {count: cantAssignLeft.length}
              )}
            </span>
          )}
          {!leftoverBusy && (
            <button
              type='button'
              className='step4-alloc-leftover__close'
              aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
              onClick={onClearLastImport}
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </div>
        {cantAssignLeft.length > 0 && (
          <div className='step4-alloc-leftover__cant'>
            {reviewLeftCount > 0 && (
              <p className='step4-alloc-leftover__title'>
                {intl.formatMessage(
                  {id: 'DATA_UPLOAD.QUESTIONS.LINES_CANT_BE_ASSIGNED'},
                  {count: cantAssignLeft.length}
                )}
              </p>
            )}
            <ul>
              {cantAssignLeft.map((line, index) => (
                <li key={`${line.teacher}-${line.written}-${index}`}>
                  <span className='fw-semibold'>{line.teacher}</span>
                  {' · '}
                  {line.message || line.written}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    ) : null

  // The shared uploader, as on Add Teachers: drop zone, limits, rows, progress, retry.
  const sharedUploader = uploader ? (
    <div className='step4-upload-scope onboarding-uploader-scope'>
      <OnboardingUploader
        context='allocation'
        files={uploaderFiles()}
        head={uploaderHead()}
        onFiles={uploader.onFiles}
        onRemove={removeLine}
        onRetry={retryRead}
        onReplace={removeLine}
        busy={importing || reading || addingBusy}
        // A read or import in flight: new files wait (they are read together with these).
        disabled={
          uploader.disabled ||
          importing ||
          refreshing ||
          addingBusy ||
          (Boolean(file) && phase === 'uploading')
        }
        accept={uploader.accept}
        formatsText={uploader.formatsText}
        limitsText={uploader.limitsText}
        onDownloadTemplate={uploader.onDownloadTemplate}
        templateBusy={uploader.templateBusy}
        rejected={uploader.rejected}
        onDismissRejected={uploader.onDismissRejected}
        guidance={uploader.guidance}
      >
        {lineReports}
      </OnboardingUploader>
      {leftoverNotice}
    </div>
  ) : null

  return (
    <>
      {sharedUploader ? (
        // In the landing's Upload card when the page gives one (nothing until it is mounted).
        uploaderTarget === undefined ? (
          sharedUploader
        ) : uploaderTarget ? (
          createPortal(sharedUploader, uploaderTarget)
        ) : null
      ) : (
        <>
          <UploadStatusPanel items={items} busy={cardBusy} onRemove={removeLine}>
            {lineReports}
          </UploadStatusPanel>
          {leftoverNotice}
        </>
      )}

      {/* Map columns, for every file: our template (or a converted file) shows how it was
          mapped and imports from here; a spreadsheet in another layout is matched by hand, then
          reviewed and imported. Closing it keeps the file; its line reopens it.
          `inlineMapper` (redesigned page): a card under the file's line. Otherwise the earlier
          dialog — no bg-dark there: Metronic's dark theme INVERTS the `dark` semantic color
          ($dark-dark: #f9f9f9), so bg-dark painted the modal light exactly in dark mode. */}
      {inlineMapper ? (
        mapperShown && (
          <section className='ta-card ta-map-panel' aria-label={mapperStepLabel}>
            <header className='ta-map-panel__head'>
              <div className='ta-map-panel__titles'>
                <h2 className='ta-map-panel__title'>
                  {intl.formatMessage({id: 'CSV_IMPORT.UPLOAD.TITLE'})}
                </h2>
                <span className='ta-map-panel__step'>{mapperStepLabel}</span>
              </div>
              {!importing && (
                <button
                  type='button'
                  className='ta-icon-btn'
                  aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
                  onClick={() => setMapperOpen(false)}
                >
                  <X size={18} aria-hidden />
                </button>
              )}
            </header>
            <div className='ta-map-panel__body'>{mapperBody}</div>
            {mapperActions.length > 0 && (
              <footer className='ta-map-panel__foot'>{mapperActions}</footer>
            )}
          </section>
        )
      ) : (
        <Modal
          show={mapperShown}
          onHide={importing ? () => {} : () => setMapperOpen(false)}
          size='xl'
          centered
          backdrop='static'
        >
          <Modal.Header closeButton={!importing}>
            <Modal.Title style={{color: textColor}}>
              {intl.formatMessage({id: 'CSV_IMPORT.UPLOAD.TITLE'})}
              <span
                className='ms-3'
                style={{color: mutedColor, fontSize: '0.85rem', fontWeight: 500}}
              >
                {mapperStepLabel}
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>{mapperBody}</Modal.Body>
          <Modal.Footer>{mapperActions}</Modal.Footer>
        </Modal>
      )}

      {/* The last import's full answer (per-table rows, errors, missing references), on
          request from its line. */}
      <ImportResultModal
        show={detailsOpen && Boolean(lastImport)}
        onHide={() => setDetailsOpen(false)}
        result={lastImport?.result}
        onClose={() => setDetailsOpen(false)}
        disableDoneNavigation
      />

      {/* The people the file names that the school has not saved, in one dialog: teachers
          (save and assign, save only, or not) and students (save only, save and choose their
          classes and subjects on the Students step, or not). */}
      <NewPeopleModal
        show={
          Boolean(file) && Boolean(newPeopleQuestion) && parkedQuestion !== newPeopleQuestion
        }
        teachers={newPeopleQuestion?.teachers ?? NO_NEW_TEACHERS}
        canContinueWithout={newPeopleQuestion?.canContinueWithout ?? false}
        // "Grade 1 A / Urdu": the file gives class, section and subject — allocated as written.
        // A line with less ("Urdu"): its classes and sections are asked on the re-read.
        assignScope={
          (newPeopleQuestion?.teachers ?? []).every(
            (teacher) =>
              teacher.samples.length > 0 &&
              teacher.samples.every((sample) => sample.includes(' / '))
          )
            ? 'lines'
            : 'classes'
        }
        // Teachers only here: the file's students are asked about once its allocations are in.
        students={NO_NEW_STUDENTS}
        submitting={savingNewTeachers}
        // "Match to existing" for names the server thinks are saved teachers (`suggestions`).
        matchable
        heldLines={heldLinesByTeacher}
        // "Don't save" answers (nobody saved, their lines left out) and reads once more; the X
        // keeps the question on the file's line, with Answer, and reads nothing.
        onCancel={() =>
          answerNewPeople({teachers: 'none', teacherKeys: [], teacherMatches: [], students: 'none'})
        }
        onClose={() => setParkedQuestion(newPeopleQuestion)}
        onConfirm={answerNewPeople}
      />

      {/* QA 555: a file with only teachers — "These teachers aren't in your list". Cancel leaves
          the card as it is (Import teachers opens this again); nothing is imported. */}
      <CarriedTeachersModal
        show={
          Boolean(file) &&
          carriedTeachersPopup &&
          Boolean(carriedTeachersRead) &&
          !carriedTeachersRead?.imported
        }
        rows={carriedTeachersRead?.rows ?? NO_TEACHER_ROWS}
        leftOut={carriedTeachersRead?.leftOut ?? NO_SKIPPED_ROWS}
        canChooseClasses={(carriedTeachersRead?.allocationQuestions.length ?? 0) > 0}
        onConfirm={confirmCarriedTeachers}
        onCancel={() => setCarriedTeachersPopup(false)}
      />

      {/* NEEDS_ASSIGNMENT: whose list each sheet is. The X keeps the question (the file's line
          waits, with Answer); Skip answers every sheet "Don't read it" and reads the file once
          more with that. */}
      <SheetAssignmentModal
        show={Boolean(file) && Boolean(sheetQuestion) && parkedQuestion !== sheetQuestion}
        question={sheetQuestion ? sheetChoices(sheetQuestion.question) : null}
        files={sheetQuestion ? [sheetQuestion.file] : []}
        onCancel={() => {
          setSheetQuestion(null)
          onClose()
        }}
        onClose={() => setParkedQuestion(sheetQuestion)}
        onSkip={answerSheets}
        onConfirm={answerSheets}
      />

      {/* BRANCH_REQUIRED: which branch the allocations are in (wording from `rowNoun`).
          ALLOCATION_CLASSES_REQUIRED: which classes lines with no clear class are in. The X keeps
          the questions (the file's line waits, with Answer) and reads nothing. Skip answers them
          once and reads the file again: those lines are left out ("No classes chosen") and the
          rest is imported. */}
      <ColumnAssignmentModal
        show={Boolean(file) && Boolean(branchQuestion) && parkedQuestion !== branchQuestion}
        columns={null}
        branches={branchQuestion?.questions ?? NO_BRANCH_QUESTIONS}
        allocations={branchQuestion?.allocations ?? NO_ALLOCATION_QUESTIONS}
        proposalReview={ONBOARDING_QA_OCT18}
        newTeachers={branchQuestion?.teachers ?? NO_NEW_TEACHERS}
        subjects={null}
        // Every file read together: a line names its own (`fileNames`); never the first one for all.
        files={
          branchQuestion
            ? [branchQuestion.file, ...(allocationCompanions.get(branchQuestion.file) ?? [])]
            : []
        }
        submitting={savingNewTeachers}
        onClose={() => setParkedQuestion(branchQuestion)}
        onSkip={(skipped) => {
          const pending = branchQuestion
          if (!pending) return
          // Kept on the card until the answer is back (no reading state); the dialog closes.
          setParkedQuestion(pending)
          // "Which classes?" skipped: each asked line answered with no class, so the server
          // leaves those out (never asked again; sent with every later read) and imports the
          // rest of the file, posted once more. A branch has no skip value: kept here, so the
          // re-read does not ask it again (it then says why nothing imports).
          setPlacingAnswers(true)
          // QA 22-Oct: the read these answers come back with is imported straight away.
          importAfterAnswersRef.current = pending.file
          rememberAnswers(pending.file, {
            allocations: skipped.allocations.length > 0 ? skipped.allocations : undefined,
            columns: hasKeys(skipped.columns) ? skipped.columns : undefined,
            skippedBranches: skipped.branches,
            // Not saved: skipped with the rest.
            declinedNewTeachers: pending.teachers.map((teacher) => teacher.key),
          })
          uploadFile(pending.file, pending.sheet, true)
        }}
        onConfirm={(answers) => {
          const pending = branchQuestion
          if (!pending) return
          const approved = answers.newTeachers
          // Every answer, then ONE re-post of the same file.
          const repost = () => {
            setParkedQuestion(pending)
            // The dialog closes; the card loads until the answers are read and imported — in
            // one pass (QA 22-Oct): the read they come back with is imported straight away.
            setPlacingAnswers(true)
            importAfterAnswersRef.current = pending.file
            rememberAnswers(pending.file, {
              branches: answers.branches,
              columns: answers.columns,
              allocations: answers.allocations,
              approvedNewTeachers: approved,
              declinedNewTeachers: pending.teachers
                .map((teacher) => teacher.key)
                .filter((key) => !approved.includes(key)),
            })
            uploadFile(pending.file, pending.sheet, true)
          }
          if (approved.length === 0) {
            repost()
            return
          }
          // The ticked teachers are saved first (the wizard's teacher save, as from the
          // new-teachers popup), so the re-post matches their lines.
          setSavingNewTeachers(true)
          // The card loads through the save and the re-read (until `settleQuiet`).
          setAddingTeachers(approved.length)
          onSaveNewTeachers(pending.teachers.filter((teacher) => approved.includes(teacher.key)))
            .then((created) => {
              noteCreatedTeachers(pending.file, created)
              setSavingNewTeachers(false)
              repost()
            })
            // The save said why; the dialog stays open to try again.
            .catch(() => {
              setSavingNewTeachers(false)
              setAddingTeachers(null)
            })
        }}
      />

      {/* QA 22-Oct: "N need your review" under the cleared card — those lines only. Confirm
          reads the answers and imports them in one pass; Skip and the X leave them waiting. */}
      <ColumnAssignmentModal
        show={leftoverOpen && reviewLeftCount > 0 && !leftoverBusy}
        columns={null}
        branches={NO_BRANCH_QUESTIONS}
        allocations={lastImport?.reviewLeft ?? NO_ALLOCATION_QUESTIONS}
        proposalReview={ONBOARDING_QA_OCT18}
        newTeachers={NO_NEW_TEACHERS}
        subjects={null}
        files={lastImport ? [lastImport.file] : []}
        submitting={false}
        onClose={() => setLeftoverOpen(false)}
        onSkip={() => setLeftoverOpen(false)}
        onConfirm={(answers) => answerLeftovers(answers.allocations)}
      />
    </>
  )
}

export default AllocationFileImport
